import React, { useState, useEffect, useRef, useCallback } from 'react';
import logoMap from './Logo - MAP-Géstion.png';
import { PrintDevis, PrintFacture, PrintAvoir, PrintLivreRecettes } from './PrintView';
import Agenda from './Agenda';
import FicheAffaire from './FicheAffaire';
import FicheClient from './FicheClient';
import FicheDevis from './FicheDevis';
import Login from './Login';
import Administration from './Administration';
import ModalEmail from './ModalEmail';
import Documentation from './components/Documentation';
import LivreRecettes from './LivreRecettes';
import ExportComptable from './ExportComptable';
import FacturesRecues from './FacturesRecues';
import AdresseAutocomplete from './AdresseAutocomplete';
import { URSSAF } from './company';

import API from './config';
import { useIsMobile, BREAKPOINT_NAV } from './useResponsive';
const UNITES = ['forfait', 'ml', 'm²', 'm³', 'u', 'h', 'j', 'lot', 'ensemble'];

const COLORS = {
  bleu: '#1E3A5F', brun: '#7A4E2D', fond: '#F7F8FA', texte: '#1F2937',
  modules: { clients:'#1E3A5F', affaires:'#7A4E2D', devis:'#F97316', factures:'#16A34A', agenda:'#7C3AED' }
};

const INACTIVITE_MS = 30 * 60 * 1000;

function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
      <div style={{ background:'white', borderRadius:'8px', padding:'24px', width: wide ? '750px' : '500px', maxHeight:'85vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
          <h3 style={{ color:COLORS.bleu, margin:0 }}>{title}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:'20px', cursor:'pointer', color:'#888' }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function App() {
  const [clients, setClients] = useState([]);
  const [affaires, setAffaires] = useState([]);
  const [devis, setDevis] = useState([]);
  const [factures, setFactures] = useState([]);
  const [avoirs, setAvoirs] = useState([]);
  const [evenements, setEvenements] = useState([]);
  const [pilotage, setPilotage] = useState(null);
  const [page, setPage] = useState('dashboard');
  const isMobile = useIsMobile();                   // <=768px : listes en cartes empilees
  const navCollapsed = useIsMobile(BREAKPOINT_NAV); // <=1024px : nav repliee en menu burger
  const [navOpen, setNavOpen] = useState(false);   // etat d'ouverture du menu burger
  const [affaireSelectionnee, setAffaireSelectionnee] = useState(null);
  const [clientSelectionne, setClientSelectionne] = useState(null);
  const [devisSelectionne, setDevisSelectionne] = useState(null);
  // Navigation dans le contexte client
  const [affaireContexteClient, setAffaireContexteClient] = useState(null);
  const [devisContexteClient, setDevisContexteClient] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({});
  // Recherche entreprise par SIREN/nom (préremplissage création de fiche client pro)
  const [sirenQuery, setSirenQuery] = useState('');
  const [rechercheSiren, setRechercheSiren] = useState(false);
  const [sirenInfo, setSirenInfo] = useState(null);
  const [sirenErreur, setSirenErreur] = useState(null);
  const [lignes, setLignes] = useState([{ ordre:1, designation:'', unite:'', _libre:false, quantite:1, prix_unitaire_ht:0 }]);
  const [utilisateur, setUtilisateur] = useState(() => {
    try { return JSON.parse(localStorage.getItem('map_user')); } catch { return null; }
  });
  const [csrfToken, setCsrfToken] = useState(() => localStorage.getItem('map_csrf_token') || '');
  const peutEcrire = utilisateur?.profil !== 'consultation';

  const utilisateurRef = useRef(utilisateur);
  useEffect(() => { utilisateurRef.current = utilisateur; }, [utilisateur]);

  const deconnecter = useCallback(() => {
    // Appeler /auth/logout pour révoquer le CSRF token + supprimer cookie JWT côté serveur
    fetch(`${API}/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    }).catch(() => {});
    // Nettoyer les tokens locaux
    localStorage.removeItem('map_csrf_token');
    localStorage.removeItem('map_user');
    localStorage.removeItem('map_token');  // résidu pré-migration cookie
    setUtilisateur(null);
    setCsrfToken('');
  }, []);

  const csrfTokenRef = useRef(csrfToken);
  useEffect(() => { csrfTokenRef.current = csrfToken; }, [csrfToken]);

  const apiFetch = useCallback((url, options = {}) => {
    const headers = { ...(options.headers || {}) };
    // JWT maintenant en httpOnly cookie, pas de header Authorization needed
    // Ajouter CSRF token sur les mutations (POST, PUT, PATCH, DELETE)
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(options.method?.toUpperCase())) {
      if (csrfTokenRef.current) headers['X-CSRF-Token'] = csrfTokenRef.current;
    }
    if (!(options.body instanceof FormData) && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    return fetch(url, { ...options, credentials: 'include', headers }).then(res => {
      if (res.status === 401) { deconnecter(); }
      // Rotation CSRF : si le backend a tourné le token, le mettre à jour côté client
      const rotated = res.headers.get('X-CSRF-Token-Refreshed');
      if (rotated) {
        csrfTokenRef.current = rotated;
        setCsrfToken(rotated);
        localStorage.setItem('map_csrf_token', rotated);
      }
      return res;
    });
  }, [deconnecter]);

  // Inactivité : déconnexion après 30 min, refresh token si actif
  const inactiviteTimer = useRef(null);
  const dernierRefresh = useRef(Date.now());

  const resetInactivite = useCallback(() => {
    if (!utilisateurRef.current) return;
    clearTimeout(inactiviteTimer.current);
    inactiviteTimer.current = setTimeout(() => deconnecter(), INACTIVITE_MS);
    // Refresh token toutes les 20 min d'activité
    if (Date.now() - dernierRefresh.current > 20 * 60 * 1000) {
      dernierRefresh.current = Date.now();
      fetch(`${API}/auth/refresh`, {
        method: 'POST',
        credentials: 'include'  // JWT en cookie (auto-envoyé par browser)
      }).catch(() => {});
    }
  }, [deconnecter]);

  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(e => window.addEventListener(e, resetInactivite, { passive: true }));
    resetInactivite();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetInactivite));
      clearTimeout(inactiviteTimer.current);
    };
  }, [resetInactivite]);

  const [maintenant, setMaintenant] = useState(new Date());
  const [rechercheClients, setRechercheClients] = useState({ texte: '', type: 'tous' });
  const [recentsOpen, setRecentsOpen] = useState(false);
  const [clientsRecents, setClientsRecents] = useState(() => {
    try { return JSON.parse(localStorage.getItem('clientsRecents') || '[]'); } catch { return []; }
  });

  const sauvegarderClientRecent = (client) => {
    const liste = [client, ...clientsRecents.filter(c => c.id !== client.id)].slice(0, 5);
    setClientsRecents(liste);
    localStorage.setItem('clientsRecents', JSON.stringify(liste));
  };
  const [rechercheAffaires, setRechercheAffaires] = useState({ texte: '', statut: 'tous' });
  const [rechercheDevis, setRechercheDevis] = useState({ texte: '', statut: 'tous' });
  const [rechercheFactures, setRechercheFactures] = useState({ texte: '', statut: 'tous' });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [printData, setPrintData] = useState(null);
  const [printType, setPrintType] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [avoirFacture, setAvoirFacture] = useState(null); // facture source pour créer un avoir
  const [avoirLignes, setAvoirLignes] = useState([]);
  const [avoirMotif, setAvoirMotif] = useState('');
  const [paiementFacture, setPaiementFacture] = useState(null); // facture en cours d'encaissement
  const [formPaiement, setFormPaiement] = useState({ montant: '', mode_paiement: 'virement', reference: '', numero_cheque: '', banque_cheque: '', tireur_cheque: '' });
  const [remboursementAvoir, setRemboursementAvoir] = useState(null); // avoir + reste_a_rembourser
  const [formRemboursement, setFormRemboursement] = useState({ montant: '', mode_paiement: 'virement', reference: '', numero_cheque: '', banque_cheque: '', tireur_cheque: '', notes: '' });
  const [refreshKeyAffaire, setRefreshKeyAffaire] = useState(0);
  const [acompteDevis, setAcompteDevis] = useState(null); // { devisId, reference, montantHT }
  const [formAcompte, setFormAcompte] = useState({ pct: 30, montant: '' });
  const [showProfil, setShowProfil] = useState(false);
  const [formProfil, setFormProfil] = useState({ ancien: '', nouveau: '', confirmation: '' });
  const [profilMsg, setProfilMsg] = useState({ texte: '', ok: true });
  const [emailDocument, setEmailDocument] = useState(null);

  const loadData = () => {
    apiFetch(`${API}/clients/`).then(r=>r.json()).then(setClients).catch(()=>setClients([]));
    apiFetch(`${API}/affaires/`).then(r=>r.json()).then(setAffaires).catch(()=>setAffaires([]));
    apiFetch(`${API}/devis/`).then(r=>r.json()).then(setDevis).catch(()=>setDevis([]));
    apiFetch(`${API}/factures/`).then(r=>r.json()).then(setFactures).catch(()=>setFactures([]));
    apiFetch(`${API}/avoirs/`).then(r=>r.json()).then(setAvoirs).catch(()=>setAvoirs([]));
    apiFetch(`${API}/agenda/`).then(r=>r.json()).then(setEvenements).catch(()=>setEvenements([]));
    apiFetch(`${API}/dashboard/pilotage`).then(r=>r.ok?r.json():null).then(setPilotage).catch(()=>setPilotage(null));
  };

  // Chargement initial des données. loadData re-créée à chaque rendu : la mettre en dep créerait une boucle.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, []);
  useEffect(() => {
    const timer = setInterval(() => setMaintenant(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const showMsg = (msg) => { setMessage(msg); setTimeout(() => setMessage(''), 3000); };
  const showErreur = (statut, detail) => {
    const code = `ERR-${statut || '000'}`;
    const msg = detail || 'Erreur inattendue';
    alert(`${msg}\n\nCode erreur : ${code}\n(Communiquez ce code au support si le problème persiste)`);
  };
  const addLigne = () => setLignes([...lignes, { ordre:lignes.length+1, designation:'', unite:'', _libre:false, quantite:1, prix_unitaire_ht:0 }]);
  const removeLigne = (i) => setLignes(lignes.filter((_,idx) => idx!==i));
  const updateLigne = (i, field, val) => setLignes(lignes.map((l,idx) => idx===i ? {...l, [field]:val} : l));
  const totalHT = () => lignes.reduce((sum,l) => sum + (parseFloat(l.quantite)||0) * (parseFloat(l.prix_unitaire_ht)||0), 0);

  const imprimerDevis = async (devisId) => {
    const d = await apiFetch(`${API}/devis/${devisId}/detail`).then(r=>r.json());
    const affaire = affaires.find(a => a.id === d.affaire_id);
    const clientAffaire = await apiFetch(`${API}/clients/${affaire?.client_id}`).then(r=>r.json()).catch(()=>null);
    setPrintData({ devis: d, affaire, client: clientAffaire, version: d.version });
    setPrintType('devis');
    setShowPreview(true);
  };

  const imprimerFacture = async (factureId) => {
    const f = await apiFetch(`${API}/factures/${factureId}/detail`).then(r=>r.json());
    const affaire = affaires.find(a => a.id === f.affaire_id);
    const clientAffaire = await apiFetch(`${API}/clients/${affaire?.client_id}`).then(r=>r.json()).catch(()=>null);
    setPrintData({ facture: f, affaire, client: clientAffaire });
    setPrintType('facture');
    setShowPreview(true);
  };

  const ouvrirModalEmail = async (type, documentId) => {
    try {
      let document, affaire, client;
      if (type === 'devis') {
        document = await apiFetch(`${API}/devis/${documentId}/detail`).then(r => r.json());
        affaire = affaires.find(a => a.id === document.affaire_id);
        client = await apiFetch(`${API}/clients/${affaire?.client_id}`).then(r => r.json()).catch(() => null);
      } else if (type === 'facture') {
        document = await apiFetch(`${API}/factures/${documentId}/detail`).then(r => r.json());
        affaire = affaires.find(a => a.id === document.affaire_id);
        client = await apiFetch(`${API}/clients/${affaire?.client_id}`).then(r => r.json()).catch(() => null);
      } else if (type === 'avoir') {
        document = await apiFetch(`${API}/avoirs/${documentId}/detail`).then(r => r.json());
        affaire = affaires.find(a => a.id === document.affaire_id);
        client = await apiFetch(`${API}/clients/${affaire?.client_id}`).then(r => r.json()).catch(() => null);
      }
      setEmailDocument({ type, document, affaire, client });
    } catch (err) {
      console.error('Erreur chargement email:', err);
    }
  };

  // Réinitialise l'état de la recherche entreprise (à la fermeture/changement de type).
  const resetSiren = () => { setSirenQuery(''); setSirenInfo(null); setSirenErreur(null); };

  // Recherche l'entreprise par SIREN/SIRET/nom et préremplit la fiche client pro à partir
  // de la donnée officielle (zéro faute de frappe). Même endpoint que la saisie de facture
  // reçue (/entreprises/recherche). On ne remplace que les champs renvoyés par l'API.
  const rechercherEntrepriseClient = async () => {
    const q = (sirenQuery || '').trim();
    if (q.length < 3) { setSirenErreur("Saisissez un SIREN (9 chiffres), un SIRET ou un nom d'entreprise"); return; }
    setRechercheSiren(true); setSirenErreur(null); setSirenInfo(null);
    try {
      const res = await apiFetch(`${API}/entreprises/recherche?q=${encodeURIComponent(q)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Recherche impossible');
      setForm(f => ({
        ...f,
        nom: data.denomination || f.nom,
        siret: data.siret_siege || f.siret,
        adresse: data.adresse || f.adresse,
        code_postal: data.code_postal || f.code_postal,
        ville: data.commune || f.ville,
      }));
      setSirenInfo(data);
    } catch (e) {
      setSirenErreur(e.message);
    } finally {
      setRechercheSiren(false);
    }
  };

  const creerClient = async () => {
    if (!form.nom) return alert('Le nom est obligatoire');
    setLoading(true);
    const r = await apiFetch(`${API}/clients/`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(form) });
    if (r.ok) { loadData(); setShowModal(false); setForm({}); resetSiren(); showMsg('✅ Client créé !'); }
    else { const err = await r.json().catch(()=>null); showErreur(r.status, err?.detail || 'Impossible de créer le client'); }
    setLoading(false);
  };

  const creerAffaire = async () => {
    if (!form.titre || !form.client_id) return alert('Titre et client sont obligatoires');
    setLoading(true);
    const payload = { titre: form.titre, client_id: parseInt(form.client_id), description: form.description || null, adresse_chantier: form.adresse_chantier || null, ville_chantier: form.ville_chantier || null };
    const r = await apiFetch(`${API}/affaires/`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(payload) });
    if (r.ok) { loadData(); setShowModal(false); setForm({}); showMsg('✅ Affaire créée !'); }
    else { const err = await r.json().catch(()=>null); showErreur(r.status, err?.detail || 'Impossible de créer l\'affaire'); }
    setLoading(false);
  };

  const creerDevis = async () => {
    if (!form.affaire_id) return alert('Affaire obligatoire');
    setLoading(true);
    const payload = { affaire_id: parseInt(form.affaire_id), version: { date_echeance_validite: form.date_echeance_validite || null, notes: form.notes || null, lignes: lignes.filter(l => l.designation).map((l,i) => ({ ordre: i+1, designation: l.designation, unite: l.unite || null, quantite: parseFloat(l.quantite)||1, prix_unitaire_ht: parseFloat(l.prix_unitaire_ht)||0 })) } };
    const r = await apiFetch(`${API}/devis/`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(payload) });
    if (r.ok) { loadData(); setShowModal(false); setForm({}); setLignes([{ordre:1,designation:'',unite:'',_libre:false,quantite:1,prix_unitaire_ht:0}]); showMsg('✅ Devis créé !'); }
    else { const err = await r.json().catch(()=>null); showErreur(r.status, err?.detail || 'Impossible de créer le devis'); }
    setLoading(false);
  };

  const creerFacture = async () => {
    if (!form.affaire_id) return alert('Affaire obligatoire');
    setLoading(true);
    const payload = { affaire_id: parseInt(form.affaire_id), devis_id: form.devis_id ? parseInt(form.devis_id) : null, notes: form.notes || null, lignes: lignes.filter(l => l.designation).map((l,i) => ({ ordre: i+1, designation: l.designation, unite: l.unite || null, quantite: parseFloat(l.quantite)||1, prix_unitaire_ht: parseFloat(l.prix_unitaire_ht)||0 })) };
    const r = await apiFetch(`${API}/factures/`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(payload) });
    if (r.ok) {
      const facture = await r.json();
      await apiFetch(`${API}/factures/${facture.id}/emettre`, { method:'PATCH' });
      // Marquer le devis comme facturé s'il était lié
      if (form.devis_id) await apiFetch(`${API}/devis/${form.devis_id}/facturer`, { method:'PATCH' });
      loadData(); setShowModal(false); setForm({}); setLignes([{ordre:1,designation:'',unite:'',_libre:false,quantite:1,prix_unitaire_ht:0}]); showMsg('✅ Facture émise !');
    } else { const err = await r.json().catch(()=>null); showErreur(r.status, err?.detail || 'Impossible de créer la facture'); }
    setLoading(false);
  };

  const cloturerAffaire = async (affaireId) => {
    const r = await apiFetch(`${API}/affaires/${affaireId}/cloturer`, { method: 'PATCH' });
    if (r.ok) { loadData(); showMsg('🏁 Affaire clôturée !'); }
    else showErreur(null, 'Erreur lors de la clôture de l\'affaire');
  };

  const enregistrerPaiement = async () => {
    const montant = parseFloat(formPaiement.montant);
    if (!montant || montant <= 0) { alert('Montant invalide'); return; }
    if (montant > parseFloat(paiementFacture.solde_restant)) { alert(`Le montant dépasse le solde restant (${parseFloat(paiementFacture.solde_restant).toFixed(2)} €)`); return; }
    if (formPaiement.mode_paiement === 'cheque') {
      if (!formPaiement.numero_cheque.trim() || !formPaiement.banque_cheque.trim() || !formPaiement.tireur_cheque.trim()) {
        alert('Pour un chèque, le numéro, la banque et le nom du tireur sont obligatoires.'); return;
      }
    }
    if (formPaiement.mode_paiement === 'especes' && montant > 1000) {
      alert('Plafond légal espèces dépassé : maximum 1 000 € par transaction (art. L112-6 CMF).'); return;
    }
    setLoading(true);
    const payload = {
      montant,
      mode_paiement: formPaiement.mode_paiement,
      reference: formPaiement.reference || null,
      numero_cheque: formPaiement.mode_paiement === 'cheque' ? formPaiement.numero_cheque : null,
      banque_cheque: formPaiement.mode_paiement === 'cheque' ? formPaiement.banque_cheque : null,
      tireur_cheque: formPaiement.mode_paiement === 'cheque' ? formPaiement.tireur_cheque : null,
    };
    const r = await apiFetch(`${API}/factures/${paiementFacture.id}/paiements`, {
      method: 'POST', headers: {'Content-Type':'application/json'},
      body: JSON.stringify(payload)
    });
    if (r.ok) {
      loadData(); setPaiementFacture(null);
      setFormPaiement({ montant:'', mode_paiement:'virement', reference:'', numero_cheque:'', banque_cheque:'', tireur_cheque:'' });
      setRefreshKeyAffaire(k => k + 1);
      showMsg('✅ Paiement enregistré !');
    } else { const err = await r.json().catch(()=>null); showErreur(r.status, err?.detail); }
    setLoading(false);
  };

  const ouvrirModalRemboursement = async (avoirId) => {
    const r = await apiFetch(`${API}/avoirs/${avoirId}/remboursements`);
    if (!r.ok) { showErreur(r.status, 'Impossible de charger les remboursements'); return; }
    const data = await r.json();
    const reste = parseFloat(data.reste_a_rembourser);
    if (reste <= 0) { alert('Cet avoir est déjà entièrement remboursé.'); return; }
    setRemboursementAvoir({ ...data, reste_a_rembourser: reste });
    setFormRemboursement({ montant: reste.toFixed(2), mode_paiement: 'virement', reference: '', numero_cheque: '', banque_cheque: '', tireur_cheque: '', notes: '' });
  };

  const enregistrerRemboursement = async () => {
    const montant = parseFloat(formRemboursement.montant);
    if (!montant || montant <= 0) { alert('Montant invalide'); return; }
    if (montant > remboursementAvoir.reste_a_rembourser) { alert(`Le montant dépasse le solde à rembourser (${remboursementAvoir.reste_a_rembourser.toFixed(2)} €)`); return; }
    if (formRemboursement.mode_paiement === 'cheque') {
      if (!formRemboursement.numero_cheque.trim() || !formRemboursement.banque_cheque.trim() || !formRemboursement.tireur_cheque.trim()) {
        alert('Pour un chèque, le numéro, la banque et le nom du tireur sont obligatoires.'); return;
      }
    }
    if (formRemboursement.mode_paiement === 'especes' && montant > 1000) {
      alert('Plafond légal espèces dépassé : maximum 1 000 € par transaction (art. L112-6 CMF).'); return;
    }
    setLoading(true);
    const payload = {
      montant,
      mode_paiement: formRemboursement.mode_paiement,
      reference: formRemboursement.reference || null,
      numero_cheque: formRemboursement.mode_paiement === 'cheque' ? formRemboursement.numero_cheque : null,
      banque_cheque: formRemboursement.mode_paiement === 'cheque' ? formRemboursement.banque_cheque : null,
      tireur_cheque: formRemboursement.mode_paiement === 'cheque' ? formRemboursement.tireur_cheque : null,
      notes: formRemboursement.notes || null,
    };
    const r = await apiFetch(`${API}/avoirs/${remboursementAvoir.avoir_id}/remboursements`, {
      method: 'POST', headers: {'Content-Type':'application/json'},
      body: JSON.stringify(payload)
    });
    if (r.ok) {
      loadData(); setRemboursementAvoir(null);
      setFormRemboursement({ montant:'', mode_paiement:'virement', reference:'', numero_cheque:'', banque_cheque:'', tireur_cheque:'', notes:'' });
      showMsg('✅ Remboursement enregistré !');
    } else { const err = await r.json().catch(()=>null); showErreur(r.status, err?.detail); }
    setLoading(false);
  };

  const ouvrirModalAcompte = async (devisId) => {
    const d = await apiFetch(`${API}/devis/${devisId}/detail`).then(r => r.json());
    const montantHT = parseFloat(d.version?.montant_ht || 0);
    const montantAcompte = (montantHT * 0.30).toFixed(2);
    setAcompteDevis({ devisId, reference: d.reference, affaireId: d.affaire_id, montantHT });
    setFormAcompte({ pct: 30, montant: montantAcompte });
  };

  const creerAcompte = async () => {
    if (!acompteDevis || !formAcompte.montant || parseFloat(formAcompte.montant) <= 0) return;
    setLoading(true);
    try {
      const r = await apiFetch(`${API}/factures/acompte`, {
        method: 'POST',
        body: JSON.stringify({
          affaire_id: acompteDevis.affaireId,
          devis_id: acompteDevis.devisId,
          montant_ht: parseFloat(formAcompte.montant),
          reference_devis: acompteDevis.reference
        })
      });
      if (r.ok) {
        loadData();
        setAcompteDevis(null);
        showMsg('✅ Facture d\'acompte émise !');
      } else {
        const err = await r.json().catch(() => null);
        showErreur(r.status, err?.detail || 'Erreur lors de l\'émission de l\'acompte');
      }
    } catch (e) {
      showErreur(null, e?.message || 'Erreur réseau');
    } finally {
      setLoading(false);
    }
  };

  const emettreFature = async (id) => {
    const r = await apiFetch(`${API}/factures/${id}/emettre`, { method:'PATCH' });
    if (r.ok) { loadData(); showMsg('✅ Facture émise !'); }
    else { const err = await r.json().catch(()=>null); showErreur(r.status, err?.detail || 'Impossible d\'émettre la facture'); }
  };

  const supprimerDevis = async (devisId) => {
    const r = await apiFetch(`${API}/devis/${devisId}`, { method: 'DELETE' });
    if (r.ok) { loadData(); setDevisSelectionne(null); setDevisContexteClient(null); showMsg('🗑️ Devis supprimé'); }
    else { const err = await r.json().catch(()=>null); showErreur(r.status, err?.detail || 'Erreur lors de la suppression'); }
  };

  const modifierEvenement = async (id, data) => {
    const r = await apiFetch(`${API}/agenda/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
    if (r.ok) { loadData(); showMsg('✅ Événement modifié !'); }
    else { const err = await r.json().catch(()=>null); showErreur(r.status, err?.detail); }
  };

  const creerEvenement = async (data) => {
    const r = await apiFetch(`${API}/agenda/`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(data) });
    if (r.ok) { loadData(); showMsg('✅ Événement créé !'); }
    else { const err = await r.json().catch(()=>null); showErreur(r.status, err?.detail || 'Impossible de créer l\'événement'); }
  };

  const supprimerEvenement = async (id) => {
    const r = await apiFetch(`${API}/agenda/${id}`, { method:'DELETE' });
    if (r.ok) { loadData(); showMsg('🗑️ Événement supprimé'); }
    else { const err = await r.json().catch(()=>null); showErreur(r.status, err?.detail || 'Impossible de supprimer l\'événement'); }
  };

  const s = {
    app: { fontFamily: 'Inter, Arial, sans-serif', minHeight: '100vh', background: COLORS.fond },
    header: { background: COLORS.bleu, padding: '0 24px', display: 'flex', alignItems: 'center', gap: '4px', boxShadow:'0 2px 8px rgba(0,0,0,0.2)' },
    logoImg: { height: '48px', marginRight: '16px', padding: '4px 0' },
    navBtn: (active, color) => ({ background: active ? 'rgba(255,255,255,0.15)' : 'transparent', color: 'white', border: 'none', padding: '16px 18px', cursor: 'pointer', fontSize: '14px', fontWeight: active ? 'bold' : 'normal', borderBottom: active ? `3px solid ${color||'white'}` : '3px solid transparent' }),
    // --- Navigation mobile (menu burger) ---
    burger: { marginLeft: 'auto', background: 'rgba(255,255,255,0.12)', color: 'white', border: 'none', fontSize: '22px', lineHeight: 1, padding: '8px 14px', borderRadius: '8px', cursor: 'pointer' },
    mobileMenu: { background: COLORS.bleu, boxShadow: '0 6px 16px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column', padding: '8px 0' },
    mobileNavBtn: (active, color) => ({ background: active ? 'rgba(255,255,255,0.15)' : 'transparent', color: 'white', border: 'none', borderLeft: active ? `4px solid ${color||'white'}` : '4px solid transparent', padding: '14px 22px', cursor: 'pointer', fontSize: '15px', fontWeight: active ? 'bold' : 'normal', textAlign: 'left', width: '100%' }),
    content: { padding: '28px', maxWidth: '1200px', margin: '0 auto' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '16px', marginBottom: '32px' },
    card: (color) => ({ background: 'white', borderRadius: '10px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderTop: `4px solid ${color}`, cursor:'pointer' }),
    cardNum: (color) => ({ fontSize: '36px', fontWeight: 'bold', color }),
    cardLabel: { color: '#6B7280', fontSize: '13px', marginTop: '4px' },
    // --- Bandeau de pilotage (responsive : reflow auto en mobile) ---
    kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '16px', marginBottom: '20px' },
    kpiCard: (color) => ({ background: 'white', borderRadius: '12px', padding: '18px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: `5px solid ${color}` }),
    kpiVal: (color) => ({ fontSize: '26px', fontWeight: 'bold', color, lineHeight: 1.1, wordBreak: 'break-word' }),
    kpiLabel: { color: '#6B7280', fontSize: '12px', marginTop: '6px', textTransform: 'uppercase', letterSpacing: '0.03em' },
    kpiSub: (color) => ({ color, fontSize: '12px', marginTop: '6px', fontWeight: '600' }),
    chartWrap: { background: 'white', borderRadius: '12px', padding: '18px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '24px' },
    chartBars: { display: 'flex', alignItems: 'flex-end', gap: '6px', height: '110px', marginTop: '14px' },
    // --- Jauge des seuils micro-entreprise (TVA / plafond micro) ---
    jaugeWrap: { background: 'white', borderRadius: '12px', padding: '18px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '24px' },
    jaugeRow: { marginTop: '16px' },
    jaugeTrack: { position: 'relative', height: '14px', background: '#F3F4F6', borderRadius: '8px', overflow: 'hidden', marginTop: '8px' },
    jaugeFill: (color, pct) => ({ height: '100%', width: `${pct}%`, background: color, borderRadius: '8px', transition: 'width .4s' }),
    jaugeMark: (pct) => ({ position: 'absolute', top: 0, bottom: 0, left: `calc(${pct}% - 1px)`, width: '2px', background: '#111827' }),
    // --- Carte de liste (vue mobile des tableaux) ---
    mCard: (color) => ({ background: 'white', borderRadius: '12px', padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.1)', borderLeft: `4px solid ${color}` }),
    mCardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' },
    mCardActions: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' },
    table: { width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
    th: (color) => ({ background: color||COLORS.bleu, color: 'white', padding: '12px 16px', textAlign: 'left', fontSize: '13px' }),
    td: { padding: '12px 16px', borderBottom: '1px solid #F3F4F6', fontSize: '14px' },
    badge: (s) => ({ display:'inline-block', padding:'3px 10px', borderRadius:'20px', fontSize:'12px', fontWeight:'600',
      background: s==='ouverte'||s==='en_cours' ? '#DCFCE7' : s==='emise' ? '#DBEAFE' : s==='brouillon' ? '#FEF9C3' : s==='annulee'||s==='refuse'||s==='expire' ? '#FEE2E2' : s==='payee'||s==='accepte' ? '#EDE9FE' : '#F3F4F6',
      color: s==='ouverte'||s==='en_cours' ? '#16A34A' : s==='emise' ? '#1D4ED8' : s==='brouillon' ? '#CA8A04' : s==='annulee'||s==='refuse'||s==='expire' ? '#DC2626' : s==='payee'||s==='accepte' ? '#7C3AED' : '#6B7280'
    }),
    h2: { color: COLORS.bleu, marginBottom: '20px', fontSize:'20px', fontWeight:'700' },
    btn: (color) => ({ background: color||COLORS.bleu, color: 'white', border: 'none', padding: '10px 18px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }),
    btnSm: (color) => ({ background: color||'#E5E7EB', color: color ? 'white' : '#374151', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }),
    input: { width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px', marginBottom: '10px', boxSizing: 'border-box' },
    inputSm: { width: '100%', padding: '6px 8px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' },
    label: { display: 'block', fontSize: '13px', color: '#374151', marginBottom: '4px', fontWeight: '600' },
    pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    msg: { position:'fixed', top:'20px', right:'20px', background:COLORS.bleu, color:'white', padding:'12px 20px', borderRadius:'8px', zIndex:2000, fontSize:'14px' }
  };

  const lignesForm = (color) => (
    <div style={{marginTop:'16px'}}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px'}}>
        <label style={{...s.label, margin:0}}>Lignes de prestation</label>
        <button style={s.btnSm(color)} onClick={addLigne}>+ Ligne</button>
      </div>
      <table style={{width:'100%', borderCollapse:'collapse'}}>
        <thead><tr style={{background:'#F3F4F6'}}>{['Désignation','Unité','Qté','Prix HT','Total',''].map(h => <th key={h} style={{padding:'8px', textAlign:'left', fontSize:'12px', color:'#374151', fontWeight:'600'}}>{h}</th>)}</tr></thead>
        <tbody>
          {lignes.map((l,i) => (
            <tr key={i}>
              <td style={{padding:'4px'}}><input style={s.inputSm} value={l.designation} onChange={e => updateLigne(i,'designation',e.target.value)} placeholder="Description" /></td>
              <td style={{padding:'4px', width:'120px'}}>
                {l._libre
                  ? <div style={{display:'flex', gap:'2px'}}>
                      <input style={{...s.inputSm, flex:1}} value={l.unite} onChange={e => updateLigne(i,'unite',e.target.value)} placeholder="Saisie libre" autoFocus />
                      <button title="Retour à la liste" style={{...s.btnSm(), padding:'4px 6px', fontSize:'11px', flexShrink:0}} onClick={() => setLignes(lignes.map((l2,idx) => idx===i ? {...l2, unite:'', _libre:false} : l2))}>↩</button>
                    </div>
                  : <select style={s.inputSm} value={l.unite} onChange={e => {
                      if (e.target.value === '__libre__') setLignes(lignes.map((l2,idx) => idx===i ? {...l2, unite:'', _libre:true} : l2));
                      else updateLigne(i,'unite',e.target.value);
                    }}>
                      <option value=''>— Unité —</option>
                      {UNITES.map(u => <option key={u} value={u}>{u}</option>)}
                      <option disabled>──────────</option>
                      <option value='__libre__'>✏️ Saisie libre...</option>
                    </select>
                }
              </td>
              <td style={{padding:'4px', width:'70px'}}><input style={s.inputSm} type="number" value={l.quantite} onChange={e => updateLigne(i,'quantite',e.target.value)} /></td>
              <td style={{padding:'4px', width:'90px'}}><input style={s.inputSm} type="number" value={l.prix_unitaire_ht} onChange={e => updateLigne(i,'prix_unitaire_ht',e.target.value)} /></td>
              <td style={{padding:'4px', width:'90px', textAlign:'right', fontWeight:'600', fontSize:'13px'}}>{((parseFloat(l.quantite)||0)*(parseFloat(l.prix_unitaire_ht)||0)).toFixed(2)} €</td>
              <td style={{padding:'4px', width:'30px'}}>{lignes.length > 1 && <button style={{...s.btnSm(), color:'#DC2626'}} onClick={() => removeLigne(i)}>✕</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{textAlign:'right', marginTop:'12px', padding:'12px', background:'#F0FDF4', borderRadius:'8px'}}>
        <span style={{fontWeight:'700', fontSize:'16px', color:COLORS.modules.factures}}>Total HT : {totalHT().toFixed(2)} €</span>
        <div style={{fontSize:'12px', color:'#6B7280', marginTop:'4px'}}>TVA non applicable, art. 293B du CGI</div>
      </div>
    </div>
  );

  const navItems = [
    { id:'dashboard', label:'🏠 Accueil' },
    { id:'clients', label:'👥 Clients', color: COLORS.modules.clients },
    { id:'affaires', label:'🔨 Affaires', color: COLORS.modules.affaires },
    { id:'devis', label:'📋 Devis', color: COLORS.modules.devis },
    { id:'factures', label:'📄 Factures', color: COLORS.modules.factures },
    { id:'factures-recues', label:'📥 Factures reçues', color: '#0D9488' },
    { id:'livre-recettes', label:'📒 Livre des recettes', color: '#0EA5E9' },
    { id:'export-comptable', label:'📊 Export comptable', color: '#1E3A5F' },
    { id:'agenda', label:'📅 Agenda', color: COLORS.modules.agenda },
    { id:'docs', label:'📚 Documentation', color: '#8B5CF6' },
  ];

  // Navigation unifiee (desktop + menu burger mobile) : change de page, reinitialise
  // les selections de contexte et referme le menu mobile.
  const allerA = (p) => {
    setPage(p);
    setAffaireSelectionnee(null);
    setClientSelectionne(null);
    setAffaireContexteClient(null);
    setDevisContexteClient(null);
    setDevisSelectionne(null);
    setNavOpen(false);
  };

  const ouvrirModalAvoir = async (factureId) => {
    const detail = await apiFetch(`${API}/factures/${factureId}/detail`).then(r => r.json());
    const lignesPrefill = (detail.lignes || []).map((l, i) => ({
      ordre: i + 1, designation: l.designation, unite: l.unite || '',
      _libre: false, quantite: parseFloat(l.quantite), prix_unitaire_ht: parseFloat(l.prix_unitaire_ht)
    }));
    setAvoirFacture(detail);
    setAvoirLignes(lignesPrefill.length > 0 ? lignesPrefill : [{ ordre: 1, designation: '', unite: '', _libre: false, quantite: 1, prix_unitaire_ht: 0 }]);
    setAvoirMotif('');
    setShowModal('avoir');
  };

  const creerAvoir = async () => {
    if (!avoirMotif.trim()) { alert('Le motif est obligatoire'); return; }
    if (avoirLignes.every(l => !l.designation)) { alert('Au moins une ligne est requise'); return; }
    const payload = {
      facture_origine_id: avoirFacture.id,
      motif: avoirMotif,
      lignes: avoirLignes.filter(l => l.designation).map(l => ({
        ordre: l.ordre, designation: l.designation, unite: l.unite || null,
        quantite: l.quantite, prix_unitaire_ht: l.prix_unitaire_ht
      }))
    };
    setLoading(true);
    const r = await apiFetch(`${API}/avoirs/`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    if (r.ok) {
      const res = await r.json().catch(() => null);
      loadData(); setShowModal(false); setAvoirFacture(null);
      showMsg(res?.facture_annulee ? '✅ Avoir créé — facture annulée (entièrement créditée).' : '✅ Avoir créé !');
    } else {
      const err = await r.json().catch(() => null);
      showErreur(r.status, err?.detail || 'Impossible de créer l\'avoir');
    }
    setLoading(false);
  };

  const imprimerAvoir = async (avoirId) => {
    const a = await apiFetch(`${API}/avoirs/${avoirId}/detail`).then(r => r.json());
    const affaire = affaires.find(af => af.id === a.affaire_id);
    const clientAffaire = clients.find(c => c.id === affaire?.client_id);
    setPrintData({ avoir: a, affaire, client: clientAffaire });
    setPrintType('avoir');
    setShowPreview(true);
  };

  const changerMonMotDePasse = async () => {
    setProfilMsg({ texte: '', ok: true });
    if (!formProfil.ancien || !formProfil.nouveau || !formProfil.confirmation) {
      setProfilMsg({ texte: 'Tous les champs sont obligatoires.', ok: false }); return;
    }
    if (formProfil.nouveau !== formProfil.confirmation) {
      setProfilMsg({ texte: 'Le nouveau mot de passe et sa confirmation ne correspondent pas.', ok: false }); return;
    }
    const r = await apiFetch(`${API}/auth/mon-profil/changer-mot-de-passe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ancien_mot_de_passe: formProfil.ancien, nouveau_mot_de_passe: formProfil.nouveau })
    });
    const data = await r.json();
    if (r.ok) {
      setProfilMsg({ texte: '✅ Mot de passe modifié avec succès !', ok: true });
      setFormProfil({ ancien: '', nouveau: '', confirmation: '' });
    } else {
      setProfilMsg({ texte: data.detail || 'Erreur', ok: false });
    }
  };

  if (!utilisateur) {
    return <Login onConnecte={(u, c) => { setUtilisateur(u); setCsrfToken(c); }} />;
  }

  return (
    <div style={s.app}>
      {message && <div style={s.msg}>{message}</div>}
      {/* Popup prévisualisation impression */}
      {showPreview && printData && (
        <div className="no-print" style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:3000, display:'flex', flexDirection:'column'}}>
          <div style={{background:'#1E3A5F', padding:'12px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0}}>
            <span style={{color:'white', fontWeight:'700', fontSize:'15px'}}>
              👁 Prévisualisation — {printType === 'devis' ? `Devis ${printData.devis?.reference}` : printType === 'facture' ? `Facture ${printData.facture?.numero}` : printType === 'avoir' ? `Avoir ${printData.avoir?.numero}` : 'Livre des recettes'}
            </span>
            <div style={{display:'flex', gap:'10px'}}>
              <button onClick={() => window.print()} style={{background:'#16A34A', color:'white', border:'none', padding:'8px 20px', borderRadius:'8px', cursor:'pointer', fontSize:'14px', fontWeight:'700'}}>🖨️ Imprimer</button>
              <button onClick={() => { setShowPreview(false); setPrintData(null); setPrintType(null); }} style={{background:'rgba(255,255,255,0.15)', color:'white', border:'none', padding:'8px 16px', borderRadius:'8px', cursor:'pointer', fontSize:'14px'}}>✕ Fermer</button>
            </div>
          </div>
          <div style={{flex:1, overflow:'auto', background:'#E5E7EB', padding:'24px', display:'flex', justifyContent:'center'}}>
            <div style={{background:'white', boxShadow:'0 4px 24px rgba(0,0,0,0.15)', minWidth:'210mm', maxWidth:'210mm'}}>
              {printType === 'devis' && <PrintDevis {...printData} />}
              {printType === 'facture' && <PrintFacture {...printData} />}
              {printType === 'avoir' && <PrintAvoir {...printData} />}
              {printType === 'livre-recettes' && <PrintLivreRecettes {...printData} />}
            </div>
          </div>
        </div>
      )}
      {/* Rendu print (hors popup, pour window.print()) */}
      {showPreview && printData && printType === 'devis' && <div className="print-only"><PrintDevis {...printData} /></div>}
      {showPreview && printData && printType === 'facture' && <div className="print-only"><PrintFacture {...printData} /></div>}
      {showPreview && printData && printType === 'avoir' && <div className="print-only"><PrintAvoir {...printData} /></div>}
      {showPreview && printData && printType === 'livre-recettes' && <div className="print-only"><PrintLivreRecettes {...printData} /></div>}

      <header style={s.header} className="no-print">
        <img src={logoMap} alt="MAP Gestion" style={s.logoImg} />
        {!navCollapsed && (
          <>
            {navItems.map(n => (
              <button key={n.id} style={s.navBtn(page===n.id, n.color)} onClick={() => allerA(n.id)}>{n.label}</button>
            ))}
            {utilisateur?.est_admin && (
              <button style={s.navBtn(page==='admin', '#6B21A8')} onClick={() => allerA('admin')}>⚙️ Admin</button>
            )}
            <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:'12px' }}>
              <button onClick={() => { setShowProfil(true); setProfilMsg({ texte: '', ok: true }); setFormProfil({ ancien: '', nouveau: '', confirmation: '' }); }}
                style={{ background:'rgba(255,255,255,0.1)', color:'rgba(255,255,255,0.85)', border:'1px solid rgba(255,255,255,0.2)', padding:'6px 14px', borderRadius:'6px', cursor:'pointer', fontSize:'13px' }}>
                👤 {utilisateur.prenom || utilisateur.nom}
              </button>
              <button onClick={deconnecter} style={{ background:'rgba(255,255,255,0.15)', color:'white', border:'none', padding:'6px 14px', borderRadius:'6px', cursor:'pointer', fontSize:'13px' }}>Déconnexion</button>
            </div>
          </>
        )}
        {navCollapsed && (
          <button style={s.burger} onClick={() => setNavOpen(o => !o)} aria-label="Menu" aria-expanded={navOpen}>{navOpen ? '✕' : '☰'}</button>
        )}
      </header>
      {navCollapsed && navOpen && (
        <div style={s.mobileMenu} className="no-print">
          {navItems.map(n => (
            <button key={n.id} style={s.mobileNavBtn(page===n.id, n.color)} onClick={() => allerA(n.id)}>{n.label}</button>
          ))}
          {utilisateur?.est_admin && (
            <button style={s.mobileNavBtn(page==='admin', '#6B21A8')} onClick={() => allerA('admin')}>⚙️ Admin</button>
          )}
          <div style={{ borderTop:'1px solid rgba(255,255,255,0.15)', marginTop:'6px', paddingTop:'6px', display:'flex', flexDirection:'column' }}>
            <button style={s.mobileNavBtn(false)} onClick={() => { setShowProfil(true); setProfilMsg({ texte: '', ok: true }); setFormProfil({ ancien: '', nouveau: '', confirmation: '' }); setNavOpen(false); }}>👤 {utilisateur.prenom || utilisateur.nom}</button>
            <button style={s.mobileNavBtn(false)} onClick={() => { setNavOpen(false); deconnecter(); }}>🚪 Déconnexion</button>
          </div>
        </div>
      )}

      <div style={{...s.content, padding: isMobile ? '16px' : '28px'}} className="no-print">

        {page==='dashboard' && (() => {
          const aujourdhui = maintenant;
          const todayStr = aujourdhui.toISOString().slice(0, 10);
          const evtDuJour = (evenements || []).filter(e => e.date_debut && e.date_debut.slice(0, 10) === todayStr);
          const devisEcheance = devis.filter(d => d.statut_global === 'en_cours' && d.date_echeance_validite && d.date_echeance_validite <= todayStr);
          const fmtEur = (v) => Number(v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
          const evolution = pilotage?.evolution_mensuelle || [];
          const evolMax = Math.max(1, ...evolution.map(e => Number(e.ca) || 0));
          const moisCourtFr = (cle) => { const [y, m] = cle.split('-'); return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('fr-FR', { month: 'short' }); };
          return (
            <>
              {/* Hero */}
              <div style={{ background: COLORS.bleu, margin: isMobile ? '-16px -16px 24px -16px' : '-28px -28px 32px -28px', padding: isMobile ? '28px 16px' : '40px 24px', textAlign: 'center', position: 'relative' }}>
                <img src={logoMap} alt="MAP Gestion" style={{ height: '180px', marginBottom: '16px', filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.4))' }} />
                <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '16px', letterSpacing: '0.03em' }}>
                  {aujourdhui.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '28px', fontWeight: '300', marginTop: '6px', letterSpacing: '0.05em' }}>
                  {aujourdhui.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </div>
                <div style={{ position: 'absolute', bottom: '10px', right: '16px', color: 'rgba(255,255,255,0.35)', fontSize: '11px', letterSpacing: '0.05em' }}>
                  MAP Gestion {process.env.REACT_APP_VERSION || 'dev'}
                </div>
              </div>

              {/* Bandeau de pilotage de l'activité */}
              {pilotage && (
                <div style={{ marginBottom: '28px' }}>
                  <h2 style={{ fontSize: '18px', color: COLORS.bleu, margin: '0 0 14px 0' }}>📊 Pilotage de l'activité {pilotage.annee}</h2>
                  <div style={s.kpiGrid}>
                    <div style={s.kpiCard('#16A34A')}>
                      <div style={s.kpiVal('#16A34A')}>{fmtEur(pilotage.ca_encaisse?.annee)}</div>
                      <div style={s.kpiLabel}>💰 Encaissé cette année</div>
                      <div style={s.kpiSub('#6B7280')}>dont {fmtEur(pilotage.ca_encaisse?.mois)} ce mois-ci</div>
                    </div>
                    <div style={s.kpiCard('#DC2626')}>
                      <div style={s.kpiVal('#DC2626')}>{fmtEur(pilotage.encours?.total)}</div>
                      <div style={s.kpiLabel}>📄 Encours à encaisser</div>
                      {Number(pilotage.encours?.retard_30j) > 0
                        ? <div style={s.kpiSub('#DC2626')}>⚠️ {fmtEur(pilotage.encours.retard_30j)} en retard ({pilotage.encours.nb_retard})</div>
                        : <div style={s.kpiSub('#6B7280')}>{pilotage.encours?.nb_factures || 0} facture(s) en attente</div>}
                    </div>
                    <div style={s.kpiCard('#1E3A5F')}>
                      <div style={s.kpiVal('#1E3A5F')}>{fmtEur(pilotage.ca_facture_annee)}</div>
                      <div style={s.kpiLabel}>🧾 Facturé cette année</div>
                      <div style={s.kpiSub('#6B7280')}>chiffre d'affaires émis</div>
                    </div>
                    <div style={s.kpiCard('#7C3AED')}>
                      <div style={s.kpiVal('#7C3AED')}>{fmtEur(pilotage.pipeline?.montant)}</div>
                      <div style={s.kpiLabel}>🔮 Pipeline (devis acceptés)</div>
                      <div style={s.kpiSub('#6B7280')}>{pilotage.pipeline?.nb || 0} devis à facturer</div>
                    </div>
                    <div style={{...s.kpiCard('#EA580C'), cursor:'pointer'}} onClick={()=>setPage('affaires')}>
                      <div style={s.kpiVal('#EA580C')}>{pilotage.affaires_ouvertes}</div>
                      <div style={s.kpiLabel}>🔨 Affaires ouvertes</div>
                      <div style={s.kpiSub('#6B7280')}>chantiers en cours</div>
                    </div>
                  </div>
                  {evolution.length > 0 && (
                    <div style={s.chartWrap}>
                      <div style={{ fontSize: '13px', color: '#374151', fontWeight: '700' }}>Encaissements — 12 derniers mois</div>
                      <div style={s.chartBars}>
                        {evolution.map(e => {
                          const v = Number(e.ca) || 0;
                          const h = Math.max(2, Math.round((v / evolMax) * 100));
                          const estCourant = e.mois === `${pilotage.annee}-${String(maintenant.getMonth() + 1).padStart(2, '0')}`;
                          return (
                            <div key={e.mois} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }} title={`${e.mois} : ${fmtEur(v)}`}>
                              <div style={{ width: '100%', height: `${h}%`, minHeight: '2px', background: estCourant ? '#16A34A' : '#93C5FD', borderRadius: '4px 4px 0 0', transition: 'height .3s' }} />
                              <div style={{ fontSize: '10px', color: '#9CA3AF' }}>{moisCourtFr(e.mois)}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Jauge des seuils micro-entreprise (TVA 37 500 € / plafond micro 83 600 €) — données 2026 */}
              {pilotage && (() => {
                const caAnnee = Number(pilotage.ca_encaisse?.annee || 0);
                const SEUILS = [
                  { cle: 'tva', label: 'Franchise de TVA', emoji: '🧾', seuil: 37500, majore: 41250,
                    sousVert: 'Pas de TVA à facturer (franchise art. 293 B)',
                    aideDepasse: 'facturation AVEC TVA à anticiper' },
                  { cle: 'micro', label: 'Plafond micro-entreprise', emoji: '🏛️', seuil: 83600, majore: 91900,
                    sousVert: 'Marge sous le plafond micro (prestations)',
                    aideDepasse: 'sortie du régime micro si dépassé 2 ans de suite' },
                ];
                const couleur = (ca, seuil) => ca >= seuil ? '#DC2626' : (ca >= seuil * 0.8 ? '#EA580C' : '#16A34A');
                return (
                  <div style={{ marginBottom: '28px' }}>
                    <h2 style={{ fontSize: '18px', color: COLORS.bleu, margin: '0 0 14px 0' }}>🎯 Seuils micro-entreprise {pilotage.annee}</h2>
                    <div style={s.jaugeWrap}>
                      {SEUILS.map(S => {
                        const col = couleur(caAnnee, S.seuil);
                        const pctFill = Math.min(100, (caAnnee / S.majore) * 100);
                        const pctMark = (S.seuil / S.majore) * 100;
                        const reste = S.seuil - caAnnee;
                        let msg, msgCol;
                        if (caAnnee >= S.majore) { msg = `🔴 Seuil majoré dépassé (${fmtEur(S.majore)}) — ${S.aideDepasse}`; msgCol = '#DC2626'; }
                        else if (caAnnee >= S.seuil) { msg = `🔴 Seuil dépassé — ${S.aideDepasse}`; msgCol = '#DC2626'; }
                        else if (caAnnee >= S.seuil * 0.8) { msg = `🟠 Approche du seuil — reste ${fmtEur(reste)}`; msgCol = '#EA580C'; }
                        else { msg = `🟢 ${S.sousVert} — reste ${fmtEur(reste)}`; msgCol = '#16A34A'; }
                        return (
                          <div key={S.cle} style={s.jaugeRow}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '4px' }}>
                              <span style={{ fontSize: '14px', fontWeight: '700', color: '#374151' }}>{S.emoji} {S.label}</span>
                              <span style={{ fontSize: '13px', color: '#6B7280' }}>{fmtEur(caAnnee)} / {fmtEur(S.seuil)}</span>
                            </div>
                            <div style={s.jaugeTrack}>
                              <div style={s.jaugeFill(col, pctFill)} />
                              <div style={s.jaugeMark(pctMark)} title={`Seuil de base ${fmtEur(S.seuil)}`} />
                            </div>
                            <div style={{ fontSize: '12px', fontWeight: '600', color: msgCol, marginTop: '6px' }}>{msg}</div>
                          </div>
                        );
                      })}
                      <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '14px' }}>
                        Basé sur le CA encaissé {pilotage.annee}. Repère sombre = seuil de base, fin de barre = seuil majoré. Seuils 2026.
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Aide déclaration URSSAF du mois (Sprint 7.2) — CA du dernier mois complet
                  (la déclaration mensuelle porte sur le mois précédent, pas le mois en cours
                  encore partiel) + estimation des cotisations, taux réel confirmé 2026-09-13. */}
              {evolution.length >= 2 && (() => {
                const moisDernier = evolution[evolution.length - 2];
                const caMoisDernier = Number(moisDernier.ca) || 0;
                const acreActive = new Date() < new Date(URSSAF.acreFin);
                const tauxCotisations = acreActive
                  ? URSSAF.tauxPrestationsPlein * (1 - URSSAF.acreReduction)
                  : URSSAF.tauxPrestationsPlein;
                const cotisationsSociales = caMoisDernier * tauxCotisations;
                const cfp = caMoisDernier * URSSAF.tauxCfp;
                const total = cotisationsSociales + cfp;
                const [y, m] = moisDernier.mois.split('-');
                const labelMois = new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
                const finMois = new Date(Number(y), Number(m), 0).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
                return (
                  <div style={{ marginBottom: '28px' }}>
                    <h2 style={{ fontSize: '18px', color: COLORS.bleu, margin: '0 0 14px 0' }}>📆 Déclaration URSSAF — {labelMois}</h2>
                    <div style={s.jaugeWrap}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                        <div>
                          <div style={{ fontSize: '12px', color: '#6B7280', textTransform: 'uppercase' }}>CA à déclarer</div>
                          <div style={{ fontSize: '22px', fontWeight: '700', color: COLORS.bleu }}>{fmtEur(caMoisDernier)}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', color: '#6B7280', textTransform: 'uppercase' }}>Cotisations estimées</div>
                          <div style={{ fontSize: '22px', fontWeight: '700', color: '#DC2626' }}>{fmtEur(total)}</div>
                        </div>
                      </div>
                      <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '12px', lineHeight: '1.6' }}>
                        Cotisations sociales ({(tauxCotisations * 100).toFixed(2).replace('.', ',')} %{acreActive ? ' — ACRE active' : ' — taux plein, ACRE terminée'}) : {fmtEur(cotisationsSociales)}<br />
                        Contribution formation pro — CFP ({(URSSAF.tauxCfp * 100).toFixed(2).replace('.', ',')} %) : {fmtEur(cfp)}
                      </div>
                      <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '10px' }}>
                        Estimation indicative — le montant exact fait foi sur l'espace URSSAF. Échéance déclarative : avant le {finMois}.
                        {acreActive && ` ACRE (−50 % sur les cotisations sociales) jusqu'au ${new Date(URSSAF.acreFin).toLocaleDateString('fr-FR')}.`}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Cartes résumé */}
              <div style={s.grid}>
                <div style={{...s.card(COLORS.modules.clients), cursor:'pointer'}} onClick={()=>setPage('clients')}><div style={s.cardNum(COLORS.modules.clients)}>{clients.length}</div><div style={s.cardLabel}>👥 Clients</div></div>
                <div style={{...s.card(COLORS.modules.affaires), cursor:'pointer'}} onClick={()=>setPage('affaires')}><div style={s.cardNum(COLORS.modules.affaires)}>{affaires.length}</div><div style={s.cardLabel}>🔨 Affaires</div></div>
                <div style={{...s.card(COLORS.modules.devis), cursor:'pointer'}} onClick={()=>setPage('devis')}><div style={s.cardNum(COLORS.modules.devis)}>{devis.length}</div><div style={s.cardLabel}>📋 Devis</div></div>
                <div style={{...s.card(COLORS.modules.factures), cursor:'pointer'}} onClick={()=>setPage('factures')}><div style={s.cardNum(COLORS.modules.factures)}>{factures.length}</div><div style={s.cardLabel}>📄 Factures</div></div>
                <div style={{...s.card(COLORS.modules.agenda), cursor:'pointer'}} onClick={()=>setPage('agenda')}><div style={s.cardNum(COLORS.modules.agenda)}>{evenements.length}</div><div style={s.cardLabel}>📅 Événements</div></div>
              </div>

              {/* Actions du jour */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>

                {/* Agenda du jour */}
                <div style={{ background: 'white', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                  <div style={{ background: COLORS.modules.agenda, color: 'white', padding: '14px 20px', fontWeight: '700', fontSize: '15px' }}>
                    📅 Agenda du jour
                  </div>
                  <div style={{ padding: '16px' }}>
                    {evtDuJour.length === 0
                      ? <p style={{ color: '#9CA3AF', fontSize: '14px', margin: 0 }}>Aucun événement aujourd'hui</p>
                      : evtDuJour.map(e => (
                          <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F3F4F6' }}>
                            <div>
                              <div style={{ fontWeight: '600', fontSize: '14px', color: COLORS.texte }}>{e.titre}</div>
                              <div style={{ fontSize: '12px', color: '#6B7280' }}>
                                {new Date(e.date_debut).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                {e.date_fin && ` → ${new Date(e.date_fin).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`}
                              </div>
                            </div>
                            <span style={{ fontSize: '12px', color: '#6B7280' }}>{clients.find(c => c.id === e.client_id)?.nom || ''}</span>
                          </div>
                        ))
                    }
                  </div>
                </div>

                {/* Devis à échéance */}
                <div style={{ background: 'white', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                  <div style={{ background: COLORS.modules.devis, color: 'white', padding: '14px 20px', fontWeight: '700', fontSize: '15px' }}>
                    📋 Devis en attente de réponse — échéance dépassée
                  </div>
                  <div style={{ padding: '16px' }}>
                    {devisEcheance.length === 0
                      ? <p style={{ color: '#9CA3AF', fontSize: '14px', margin: 0 }}>Aucun devis en retard</p>
                      : devisEcheance.map(d => {
                          const affaire = affaires.find(a => a.id === d.affaire_id);
                          return (
                            <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F3F4F6' }}>
                              <div>
                                <div style={{ fontWeight: '600', fontSize: '14px', color: COLORS.texte }}>{d.reference}</div>
                                <div style={{ fontSize: '12px', color: '#6B7280' }}>{affaire?.titre || '-'}</div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '12px', color: '#DC2626', fontWeight: '600' }}>Échue le {new Date(d.date_echeance_validite).toLocaleDateString('fr-FR')}</div>
                              </div>
                            </div>
                          );
                        })
                    }
                  </div>
                </div>
              </div>

              {/* Affaires en cours */}
              {(() => {
                const affairesOuvertes = affaires.filter(a => a.statut === 'ouverte');
                return affairesOuvertes.length > 0 && (
                  <div style={{ background: 'white', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden', marginBottom: '24px' }}>
                    <div style={{ background: COLORS.modules.affaires, color: 'white', padding: '14px 20px', fontWeight: '700', fontSize: '15px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span>🔨 Affaires en cours ({affairesOuvertes.length})</span>
                      <button style={{ background:'rgba(255,255,255,0.2)', border:'none', color:'white', padding:'4px 12px', borderRadius:'6px', cursor:'pointer', fontSize:'13px' }} onClick={() => setPage('affaires')}>Voir tout →</button>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#F9FAFB' }}>
                          {['Référence', 'Titre', 'Client', 'Ville'].map(h => (
                            <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: '12px', color: '#374151', fontWeight: '700' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {affairesOuvertes.map((a, i) => (
                          <tr key={a.id} style={{ background: i%2===0?'white':'#F9FAFB', cursor:'pointer' }} onClick={() => { setPage('affaires'); setAffaireSelectionnee(a); }}>
                            <td style={{ ...s.td, fontWeight:'600' }}>{a.reference}</td>
                            <td style={s.td}>{a.titre}</td>
                            <td style={s.td}>{clients.find(c => c.id === a.client_id)?.nom || '—'}</td>
                            <td style={s.td}>{a.ville_chantier || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}

              {/* Factures en attente de règlement */}
              {(() => {
                const facturesImpayees = factures.filter(f => f.statut === 'emise' || f.statut === 'partiellement_payee');
                return (
                  <div style={{ background: 'white', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden', marginBottom: '16px' }}>
                    <div style={{ background: COLORS.modules.factures, color: 'white', padding: '14px 20px', fontWeight: '700', fontSize: '15px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span>📄 Factures en attente de règlement</span>
                      {facturesImpayees.length > 0 && (
                        <span style={{fontSize:'13px', background:'rgba(255,255,255,0.2)', padding:'2px 10px', borderRadius:'12px'}}>
                          {facturesImpayees.reduce((s,f) => s + parseFloat(f.solde_restant||0), 0).toFixed(2)} € à encaisser
                        </span>
                      )}
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#F9FAFB' }}>
                          {['Numéro', 'Client', 'Affaire', 'Montant HT', 'Solde restant', 'Statut', ''].map(h => (
                            <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: '12px', color: '#374151', fontWeight: '700' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {facturesImpayees.length === 0
                          ? <tr><td colSpan={7} style={{ padding: '24px', textAlign: 'center', color: '#9CA3AF', fontSize: '14px' }}>✅ Aucune facture en attente de règlement</td></tr>
                          : facturesImpayees.map((f, i) => {
                              const affaire = affaires.find(a => a.id === f.affaire_id);
                              const client = clients.find(c => c.id === affaire?.client_id);
                              return (
                                <tr key={f.id} style={{ background: i % 2 === 0 ? 'white' : '#F9FAFB' }}>
                                  <td style={{ ...s.td, fontWeight: '600' }}>{f.numero}</td>
                                  <td style={s.td}>{client?.nom || '—'}</td>
                                  <td style={s.td}>{affaire?.titre || '—'}</td>
                                  <td style={s.td}>{parseFloat(f.montant_ht).toFixed(2)} €</td>
                                  <td style={{ ...s.td, fontWeight:'700', color:'#DC2626' }}>{parseFloat(f.solde_restant).toFixed(2)} €</td>
                                  <td style={s.td}><span style={s.badge(f.statut)}>{f.statut}</span></td>
                                  <td style={s.td}>
                                    {peutEcrire && <button style={{...s.btnSm('#16A34A'), marginRight:'4px'}} onClick={() => { setPaiementFacture(f); setFormPaiement({ montant: parseFloat(f.solde_restant).toFixed(2), mode_paiement:'virement', reference:'', numero_cheque:'', banque_cheque:'', tireur_cheque:'' }); }}>💰 Encaisser</button>}
                                    <button style={{...s.btnSm('#0EA5E9'), marginRight:'4px'}} onClick={() => ouvrirModalEmail('facture', f.id)}>📧</button>
                                    <button style={s.btnSm('#6B7280')} onClick={() => imprimerFacture(f.id)}>🖨️ PDF</button>
                                  </td>
                                </tr>
                              );
                            })
                        }
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </>
          );
        })()}

        {page==='docs' && <Documentation />}

        {page==='livre-recettes' && <LivreRecettes apiFetch={apiFetch} onImprimer={(data) => { setPrintData(data); setPrintType('livre-recettes'); setShowPreview(true); }} />}
        {page==='export-comptable' && <ExportComptable apiFetch={apiFetch} />}
        {page==='factures-recues' && <FacturesRecues apiFetch={apiFetch} />}

        {page==='clients' && !clientSelectionne && (
          <>
            <div style={s.pageHeader}>
              <h2 style={{...s.h2, margin:0}}>👥 Clients</h2>
              {peutEcrire && <button style={s.btn(COLORS.modules.clients)} onClick={() => { setForm({}); setShowModal('client'); }}>+ Nouveau client</button>}
            </div>

            {/* Barre de recherche */}
            <div style={{ display:'flex', gap:'12px', marginBottom:'16px', alignItems:'center' }}>
              <input
                style={{ ...s.input, marginBottom:0, flex:1, paddingLeft:'36px', backgroundImage:'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'16\' height=\'16\' fill=\'%239CA3AF\' viewBox=\'0 0 16 16\'%3E%3Cpath d=\'M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.099zm-5.242 1.656a5.5 5.5 0 1 1 0-11 5.5 5.5 0 0 1 0 11z\'/%3E%3C/svg%3E")', backgroundRepeat:'no-repeat', backgroundPosition:'10px center' }}
                placeholder="Rechercher par nom, prénom, téléphone, ville..."
                value={rechercheClients.texte}
                onChange={e => setRechercheClients(r => ({ ...r, texte: e.target.value }))}
              />
              <select
                style={{ ...s.input, marginBottom:0, width:'160px' }}
                value={rechercheClients.type}
                onChange={e => setRechercheClients(r => ({ ...r, type: e.target.value }))}
              >
                <option value="tous">Tous les types</option>
                <option value="particulier">👤 Particulier</option>
                <option value="pro">🏢 Pro</option>
              </select>
              {(rechercheClients.texte || rechercheClients.type !== 'tous') && (
                <button style={s.btnSm()} onClick={() => setRechercheClients({ texte:'', type:'tous' })}>✕ Effacer</button>
              )}
              {/* Bouton Récents */}
              {clientsRecents.length > 0 && (
                <div style={{ position:'relative' }}>
                  <button style={s.btnSm(COLORS.modules.clients)} onClick={() => setRecentsOpen(o => !o)}>
                    🕐 Récents {recentsOpen ? '▲' : '▼'}
                  </button>
                  {recentsOpen && (
                    <div style={{ position:'absolute', right:0, top:'calc(100% + 4px)', background:'white', borderRadius:'8px', boxShadow:'0 4px 20px rgba(0,0,0,0.15)', minWidth:'220px', zIndex:100, overflow:'hidden' }}>
                      <div style={{ padding:'8px 12px', fontSize:'11px', color:'#9CA3AF', fontWeight:'600', textTransform:'uppercase', borderBottom:'1px solid #F3F4F6' }}>5 derniers clients consultés</div>
                      {clientsRecents.map(c => (
                        <div key={c.id} style={{ padding:'10px 14px', cursor:'pointer', fontSize:'14px', borderBottom:'1px solid #F9FAFB', display:'flex', alignItems:'center', gap:'8px' }}
                          onMouseEnter={e => e.currentTarget.style.background='#F0F7FF'}
                          onMouseLeave={e => e.currentTarget.style.background='white'}
                          onClick={() => { sauvegarderClientRecent(c); setClientSelectionne(c); setAffaireContexteClient(null); setDevisContexteClient(null); setRecentsOpen(false); }}>
                          <span style={{ fontSize:'16px' }}>{c.est_professionnel ? '🏢' : '👤'}</span>
                          <div>
                            <div style={{ fontWeight:'600', color:COLORS.bleu }}>{c.nom} {c.prenom||''}</div>
                            <div style={{ fontSize:'12px', color:'#9CA3AF' }}>{c.ville||'—'}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Tableau */}
            {(() => {
              const t = rechercheClients.texte.toLowerCase();
              const clientsFiltres = [...clients]
                .sort((a, b) => a.nom.localeCompare(b.nom))
                .filter(c => {
                  const matchTexte = !t || [c.nom, c.prenom, c.telephone, c.ville].some(v => v && v.toLowerCase().includes(t));
                  const matchType = rechercheClients.type === 'tous' || (rechercheClients.type === 'pro' ? c.est_professionnel : !c.est_professionnel);
                  return matchTexte && matchType;
                });
              return (
                <>
                  <div style={{ fontSize:'13px', color:'#6B7280', marginBottom:'8px' }}>
                    {clientsFiltres.length} client{clientsFiltres.length !== 1 ? 's' : ''} {clients.length !== clientsFiltres.length ? `sur ${clients.length}` : ''}
                  </div>
                  {isMobile ? (
                    <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
                      {clientsFiltres.length === 0
                        ? <div style={{ textAlign:'center', color:'#9CA3AF', padding:'32px' }}>Aucun client trouvé</div>
                        : clientsFiltres.map(c => (
                            <div key={c.id} style={{...s.mCard(COLORS.modules.clients), cursor:'pointer'}} onClick={() => { sauvegarderClientRecent(c); setClientSelectionne(c); setAffaireContexteClient(null); setDevisContexteClient(null); }}>
                              <div style={s.mCardTop}>
                                <div style={{ fontWeight:'700', fontSize:'15px' }}>{c.nom} {c.prenom||''}</div>
                                <span style={{ fontSize:'12px', color:'#6B7280' }}>{c.est_professionnel?'🏢 Pro':'👤 Particulier'}</span>
                              </div>
                              <div style={{ fontSize:'13px', color:'#6B7280', marginTop:'4px' }}>{c.telephone||'—'}{c.ville ? ` · ${c.ville}` : ''}</div>
                            </div>
                          ))
                      }
                    </div>
                  ) : (
                  <table style={s.table}>
                    <thead><tr>{['Nom','Prénom','Téléphone','Ville','Type'].map(h => <th key={h} style={s.th(COLORS.modules.clients)}>{h}</th>)}</tr></thead>
                    <tbody>
                      {clientsFiltres.length === 0
                        ? <tr><td colSpan={5} style={{...s.td, textAlign:'center', color:'#9CA3AF', padding:'32px'}}>Aucun client trouvé</td></tr>
                        : clientsFiltres.map((c, i) => (
                            <tr key={c.id} style={{background: i%2===0?"white":"#F9FAFB", cursor:"pointer"}} onClick={() => { sauvegarderClientRecent(c); setClientSelectionne(c); setAffaireContexteClient(null); setDevisContexteClient(null); }}>
                              <td style={{...s.td, fontWeight:"600"}}>{c.nom}</td>
                              <td style={s.td}>{c.prenom||"-"}</td>
                              <td style={s.td}>{c.telephone||'-'}</td>
                              <td style={s.td}>{c.ville||'-'}</td>
                              <td style={s.td}>{c.est_professionnel?'🏢 Pro':'👤 Particulier'}</td>
                            </tr>
                          ))
                      }
                    </tbody>
                  </table>
                  )}
                </>
              );
            })()}
          </>
        )}


        {/* ── Contexte client : FicheDevis ── */}
        {page==='clients' && clientSelectionne && devisContexteClient && (
          <FicheDevis
            apiFetch={apiFetch}
            devisId={devisContexteClient}
            affaires={affaires}
            clients={clients}
            peutEcrire={peutEcrire}
            refreshKey={refreshKeyAffaire}
            showErreur={showErreur}
            onRetour={() => setDevisContexteClient(null)}
            onImprimer={imprimerDevis}
            onModified={() => loadData()}
            onAcompte={ouvrirModalAcompte}
            onSupprimer={supprimerDevis}
            onEncaisser={(f) => { setPaiementFacture(f); setFormPaiement({ montant: parseFloat(f.solde_restant).toFixed(2), mode_paiement:'virement', reference:'', numero_cheque:'', banque_cheque:'', tireur_cheque:'' }); }}
            onFacturer={async (devisId) => {
              const detail = await apiFetch(`${API}/devis/${devisId}/detail`).then(r => r.json());
              const lignesPrefill = (detail.version?.lignes || []).map((l, i) => ({
                ordre: i+1, designation: l.designation, unite: l.unite||'',
                _libre: false, quantite: parseFloat(l.quantite), prix_unitaire_ht: parseFloat(l.prix_unitaire_ht)
              }));
              setForm({ affaire_id: detail.affaire_id, devis_id: devisId });
              setLignes(lignesPrefill.length > 0 ? lignesPrefill : [{ ordre:1, designation:'', unite:'', _libre:false, quantite:1, prix_unitaire_ht:0 }]);
              setDevisContexteClient(null);
              setShowModal('facture');
            }}
            onEnvoyer={() => ouvrirModalEmail('devis', devisContexteClient)}
          />
        )}

        {/* ── Contexte client : FicheAffaire ── */}
        {page==='clients' && clientSelectionne && affaireContexteClient && !devisContexteClient && (
          <FicheAffaire
            apiFetch={apiFetch}
            affaire={affaireContexteClient}
            clients={clients}
            peutEcrire={peutEcrire}
            refreshKey={refreshKeyAffaire}
            onRetour={() => setAffaireContexteClient(null)}
            onImprimerDevis={imprimerDevis}
            onImprimerFacture={imprimerFacture}
            onOuvrirDevis={(devisId) => setDevisContexteClient(devisId)}
            onNouveauDevis={() => { const d = new Date(); d.setDate(d.getDate()+60); setForm({ affaire_id: affaireContexteClient.id, date_echeance_validite: d.toISOString().slice(0,10) }); setLignes([{ordre:1,designation:'',unite:'',_libre:false,quantite:1,prix_unitaire_ht:0}]); setShowModal('devis'); }}
            onAcompte={ouvrirModalAcompte}
            onEmettre={emettreFature}
            onEncaisser={(f) => { setPaiementFacture(f); setFormPaiement({ montant: parseFloat(f.solde_restant).toFixed(2), mode_paiement:'virement', reference:'', numero_cheque:'', banque_cheque:'', tireur_cheque:'' }); }}
            onCloturer={async (affaireId) => { await cloturerAffaire(affaireId); setAffaireContexteClient(a => ({...a, statut:'cloturee'})); }}
            onModified={async () => {
              const fresh = await apiFetch(`${API}/affaires/${affaireContexteClient.id}`).then(r => r.ok ? r.json() : null).catch(() => null);
              if (fresh) setAffaireContexteClient(fresh);
              loadData();
              showMsg('✅ Affaire mise à jour !');
            }}
            onFacturer={async (devisId) => {
              const detail = await apiFetch(`${API}/devis/${devisId}/detail`).then(r => r.json());
              const lignesPrefill = (detail.version?.lignes || []).map((l, i) => ({
                ordre: i+1, designation: l.designation, unite: l.unite||'',
                _libre: false, quantite: parseFloat(l.quantite), prix_unitaire_ht: parseFloat(l.prix_unitaire_ht)
              }));
              setForm({ affaire_id: detail.affaire_id, devis_id: devisId });
              setLignes(lignesPrefill.length > 0 ? lignesPrefill : [{ ordre:1, designation:'', unite:'', _libre:false, quantite:1, prix_unitaire_ht:0 }]);
              setShowModal('facture');
            }}
            onEnvoyerEmail={(factureId) => ouvrirModalEmail('facture', factureId)}
          />
        )}

        {/* ── Contexte client : FicheClient ── */}
        {page==='clients' && clientSelectionne && !affaireContexteClient && !devisContexteClient && (
          <FicheClient
            apiFetch={apiFetch}
            clientId={clientSelectionne.id}
            affaires={affaires}
            devis={devis}
            factures={factures}
            peutEcrire={peutEcrire}
            onRetour={() => { setClientSelectionne(null); setAffaireContexteClient(null); setDevisContexteClient(null); }}
            onNouvelleAffaire={() => { setForm({client_id: clientSelectionne.id}); setShowModal('affaire'); }}
            onModified={() => loadData()}
            onOuvrirAffaire={(affaire) => setAffaireContexteClient(affaire)}
            onOuvrirDevis={(devisId) => setDevisContexteClient(devisId)}
          />
        )}

        {page==='affaires' && !affaireSelectionnee && (
          <>
            <div style={s.pageHeader}>
              <h2 style={{...s.h2, margin:0}}>🔨 Affaires</h2>
              {peutEcrire && <button style={s.btn(COLORS.modules.affaires)} onClick={() => { setForm({}); setShowModal('affaire'); }}>+ Nouvelle affaire</button>}
            </div>

            {/* Barre de recherche */}
            <div style={{ display:'flex', gap:'12px', marginBottom:'16px', alignItems:'center' }}>
              <input
                style={{ ...s.input, marginBottom:0, flex:1, paddingLeft:'36px', backgroundImage:'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'16\' height=\'16\' fill=\'%239CA3AF\' viewBox=\'0 0 16 16\'%3E%3Cpath d=\'M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.099zm-5.242 1.656a5.5 5.5 0 1 1 0-11 5.5 5.5 0 0 1 0 11z\'/%3E%3C/svg%3E")', backgroundRepeat:'no-repeat', backgroundPosition:'10px center' }}
                placeholder="Rechercher par référence, titre, client, ville..."
                value={rechercheAffaires.texte}
                onChange={e => setRechercheAffaires(r => ({ ...r, texte: e.target.value }))}
              />
              <select
                style={{ ...s.input, marginBottom:0, width:'160px' }}
                value={rechercheAffaires.statut}
                onChange={e => setRechercheAffaires(r => ({ ...r, statut: e.target.value }))}
              >
                <option value="tous">Tous les statuts</option>
                <option value="ouverte">Ouverte</option>
                <option value="cloturee">Clôturée</option>
              </select>
              {(rechercheAffaires.texte || rechercheAffaires.statut !== 'tous') && (
                <button style={s.btnSm()} onClick={() => setRechercheAffaires({ texte:'', statut:'tous' })}>✕ Effacer</button>
              )}
            </div>

            {(() => {
              const t = rechercheAffaires.texte.toLowerCase();
              const affairesFiltrees = affaires.filter(a => {
                const clientNom = clients.find(c => c.id === a.client_id)?.nom || '';
                const matchTexte = !t || [a.reference, a.titre, clientNom, a.ville_chantier].some(v => v && v.toLowerCase().includes(t));
                const matchStatut = rechercheAffaires.statut === 'tous' || a.statut === rechercheAffaires.statut;
                return matchTexte && matchStatut;
              });
              return (
                <>
                  <div style={{ fontSize:'13px', color:'#6B7280', marginBottom:'8px' }}>
                    {affairesFiltrees.length} affaire{affairesFiltrees.length !== 1 ? 's' : ''} {affaires.length !== affairesFiltrees.length ? `sur ${affaires.length}` : ''}
                  </div>
                  {isMobile ? (
                    <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
                      {affairesFiltrees.length === 0
                        ? <div style={{ textAlign:'center', color:'#9CA3AF', padding:'32px' }}>Aucune affaire trouvée</div>
                        : affairesFiltrees.map(a => (
                            <div key={a.id} style={{...s.mCard(COLORS.modules.affaires), cursor:'pointer'}} onClick={() => setAffaireSelectionnee(a)}>
                              <div style={s.mCardTop}>
                                <div style={{ fontWeight:'700', fontSize:'15px' }}>{a.reference}</div>
                                <span style={s.badge(a.statut)}>{a.statut}</span>
                              </div>
                              <div style={{ fontSize:'14px', marginTop:'4px' }}>{a.titre}</div>
                              <div style={{ fontSize:'13px', color:'#6B7280', marginTop:'4px' }}>
                                {clients.find(c=>c.id===a.client_id)?.nom||'-'}{a.ville_chantier ? ` · ${a.ville_chantier}` : ''}
                              </div>
                            </div>
                          ))
                      }
                    </div>
                  ) : (
                  <table style={s.table}>
                    <thead><tr>{['Référence','Titre','Client','Ville','Statut',''].map(h => <th key={h} style={s.th(COLORS.modules.affaires)}>{h}</th>)}</tr></thead>
                    <tbody>
                      {affairesFiltrees.length === 0
                        ? <tr><td colSpan={6} style={{...s.td, textAlign:'center', color:'#9CA3AF', padding:'32px'}}>Aucune affaire trouvée</td></tr>
                        : affairesFiltrees.map((a, i) => (
                            <tr key={a.id} style={{background: i%2===0?'white':'#F9FAFB', cursor:'pointer'}} onClick={() => setAffaireSelectionnee(a)}>
                              <td style={{...s.td, fontWeight:'600'}}>{a.reference}</td>
                              <td style={s.td}>{a.titre}</td>
                              <td style={s.td}>{clients.find(c=>c.id===a.client_id)?.nom||'-'}</td>
                              <td style={s.td}>{a.ville_chantier||'-'}</td>
                              <td style={s.td}><span style={s.badge(a.statut)}>{a.statut}</span></td>
                              <td style={s.td}><span style={{color:COLORS.modules.affaires, fontSize:'18px'}}>›</span></td>
                            </tr>
                          ))
                      }
                    </tbody>
                  </table>
                  )}
                </>
              );
            })()}
          </>
        )}

        {page==='affaires' && affaireSelectionnee && (
          <FicheAffaire
            apiFetch={apiFetch}
            affaire={affaireSelectionnee}
            clients={clients}
            peutEcrire={peutEcrire}
            refreshKey={refreshKeyAffaire}
            onRetour={() => setAffaireSelectionnee(null)}
            onImprimerDevis={imprimerDevis}
            onImprimerFacture={imprimerFacture}
            onOuvrirDevis={(devisId) => { setPage('devis'); setDevisSelectionne(devisId); }}
            onNouveauDevis={() => { const d = new Date(); d.setDate(d.getDate()+60); setForm({ affaire_id: affaireSelectionnee.id, date_echeance_validite: d.toISOString().slice(0,10) }); setLignes([{ordre:1,designation:'',unite:'',_libre:false,quantite:1,prix_unitaire_ht:0}]); setShowModal('devis'); }}
            onAcompte={ouvrirModalAcompte}
            onEmettre={emettreFature}
            onEncaisser={(f) => { setPaiementFacture(f); setFormPaiement({ montant: parseFloat(f.solde_restant).toFixed(2), mode_paiement:'virement', reference:'', numero_cheque:'', banque_cheque:'', tireur_cheque:'' }); }}
            onCloturer={async (affaireId) => { await cloturerAffaire(affaireId); setAffaireSelectionnee(a => ({...a, statut:'cloturee'})); }}
            onModified={async () => {
              const fresh = await apiFetch(`${API}/affaires/${affaireSelectionnee.id}`).then(r => r.ok ? r.json() : null).catch(() => null);
              if (fresh) setAffaireSelectionnee(fresh);
              loadData();
              showMsg('✅ Affaire mise à jour !');
            }}
            onFacturer={async (devisId) => {
              const detail = await apiFetch(`${API}/devis/${devisId}/detail`).then(r => r.json());
              const lignesPrefill = (detail.version?.lignes || []).map((l, i) => ({
                ordre: i+1, designation: l.designation, unite: l.unite||'',
                _libre: false, quantite: parseFloat(l.quantite), prix_unitaire_ht: parseFloat(l.prix_unitaire_ht)
              }));
              setForm({ affaire_id: detail.affaire_id, devis_id: devisId });
              setLignes(lignesPrefill.length > 0 ? lignesPrefill : [{ ordre:1, designation:'', unite:'', _libre:false, quantite:1, prix_unitaire_ht:0 }]);
              setPage('factures');
              setAffaireSelectionnee(null);
              setShowModal('facture');
            }}
            onEnvoyerEmail={(factureId) => ouvrirModalEmail('facture', factureId)}
          />
        )}

        {page==='devis' && !devisSelectionne && (
          <>
            <div style={s.pageHeader}>
              <h2 style={{...s.h2, margin:0}}>📋 Devis</h2>
              {peutEcrire && <button style={s.btn(COLORS.modules.devis)} onClick={() => { const d = new Date(); d.setDate(d.getDate()+60); setForm({ date_echeance_validite: d.toISOString().slice(0,10) }); setLignes([{ordre:1,designation:'',unite:'',_libre:false,quantite:1,prix_unitaire_ht:0}]); setShowModal('devis'); }}>+ Nouveau devis</button>}
            </div>

            {/* Barre de recherche */}
            <div style={{ display:'flex', gap:'12px', marginBottom:'16px', alignItems:'center' }}>
              <input
                style={{ ...s.input, marginBottom:0, flex:1, paddingLeft:'36px', backgroundImage:'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'16\' height=\'16\' fill=\'%239CA3AF\' viewBox=\'0 0 16 16\'%3E%3Cpath d=\'M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.099zm-5.242 1.656a5.5 5.5 0 1 1 0-11 5.5 5.5 0 0 1 0 11z\'/%3E%3C/svg%3E")', backgroundRepeat:'no-repeat', backgroundPosition:'10px center' }}
                placeholder="Référence, affaire, client..."
                value={rechercheDevis.texte}
                onChange={e => setRechercheDevis(r => ({ ...r, texte: e.target.value }))}
              />
              <select
                style={{ ...s.input, marginBottom:0, width:'180px' }}
                value={rechercheDevis.statut}
                onChange={e => setRechercheDevis(r => ({ ...r, statut: e.target.value }))}
              >
                <option value="tous">Tous les statuts</option>
                <option value="brouillon">Brouillon</option>
                <option value="en_cours">En cours</option>
                <option value="accepte">Accepté</option>
                <option value="refuse">Refusé</option>
                <option value="expire">Expiré</option>
              </select>
              {(rechercheDevis.texte || rechercheDevis.statut !== 'tous') && (
                <button style={s.btnSm()} onClick={() => setRechercheDevis({ texte:'', statut:'tous' })}>✕ Effacer</button>
              )}
            </div>

            {(() => {
              const t = rechercheDevis.texte.toLowerCase();
              const devisFiltres = devis.filter(d => {
                const affaire = affaires.find(a => a.id === d.affaire_id);
                const clientNom = clients.find(c => c.id === affaire?.client_id)?.nom || '';
                const matchTexte = !t || [d.reference, affaire?.titre, affaire?.reference, clientNom].some(v => v && v.toLowerCase().includes(t));
                const matchStatut = rechercheDevis.statut === 'tous' || d.statut_global === rechercheDevis.statut;
                return matchTexte && matchStatut;
              });
              return (
                <>
                  <div style={{ fontSize:'13px', color:'#6B7280', marginBottom:'8px' }}>
                    {devisFiltres.length} devis {devis.length !== devisFiltres.length ? `sur ${devis.length}` : ''}
                  </div>
                  {isMobile ? (
                    <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
                      {devisFiltres.length === 0
                        ? <div style={{ textAlign:'center', color:'#9CA3AF', padding:'32px' }}>Aucun devis trouvé</div>
                        : devisFiltres.map(d => (
                            <div key={d.id} style={s.mCard(COLORS.modules.devis)}>
                              <div style={s.mCardTop}>
                                <div style={{ fontWeight:'700', fontSize:'15px' }}>{d.reference}</div>
                                <span style={s.badge(d.statut_global)}>{d.statut_global}</span>
                              </div>
                              <div style={{ fontSize:'13px', color:'#6B7280', marginTop:'4px' }}>{affaires.find(a=>a.id===d.affaire_id)?.titre||'-'} · {new Date(d.created_at).toLocaleDateString('fr-FR')}</div>
                              <div style={s.mCardActions}>
                                <button style={s.btnSm(COLORS.modules.devis)} onClick={() => setDevisSelectionne(d.id)}>👁 Voir</button>
                                <button style={s.btnSm("#6B7280")} onClick={() => imprimerDevis(d.id)}>🖨️ PDF</button>
                              </div>
                            </div>
                          ))
                      }
                    </div>
                  ) : (
                  <table style={s.table}>
                    <thead><tr>{['Référence','Affaire','Statut','Créé le','Actions'].map(h => <th key={h} style={s.th(COLORS.modules.devis)}>{h}</th>)}</tr></thead>
                    <tbody>
                      {devisFiltres.length === 0
                        ? <tr><td colSpan={5} style={{...s.td, textAlign:'center', color:'#9CA3AF', padding:'32px'}}>Aucun devis trouvé</td></tr>
                        : devisFiltres.map((d, i) => (
                            <tr key={d.id} style={{background: i%2===0?'white':'#F9FAFB'}}>
                              <td style={{...s.td, fontWeight:'600'}}>{d.reference}</td>
                              <td style={s.td}>{affaires.find(a=>a.id===d.affaire_id)?.titre||'-'}</td>
                              <td style={s.td}><span style={s.badge(d.statut_global)}>{d.statut_global}</span></td>
                              <td style={s.td}>{new Date(d.created_at).toLocaleDateString('fr-FR')}</td>
                              <td style={s.td}><button style={{...s.btnSm(COLORS.modules.devis), marginRight:"6px"}} onClick={() => setDevisSelectionne(d.id)}>👁 Voir</button><button style={s.btnSm("#6B7280")} onClick={() => imprimerDevis(d.id)}>🖨️ PDF</button></td>
                            </tr>
                          ))
                      }
                    </tbody>
                  </table>
                  )}
                </>
              );
            })()}
          </>
        )}

        {page==='devis' && devisSelectionne && (
          <FicheDevis
            apiFetch={apiFetch}
            devisId={devisSelectionne}
            affaires={affaires}
            clients={clients}
            peutEcrire={peutEcrire}
            refreshKey={refreshKeyAffaire}
            showErreur={showErreur}
            onRetour={() => setDevisSelectionne(null)}
            onImprimer={imprimerDevis}
            onModified={() => loadData()}
            onAcompte={ouvrirModalAcompte}
            onSupprimer={supprimerDevis}
            onEncaisser={(f) => { setPaiementFacture(f); setFormPaiement({ montant: parseFloat(f.solde_restant).toFixed(2), mode_paiement:'virement', reference:'', numero_cheque:'', banque_cheque:'', tireur_cheque:'' }); }}
            onFacturer={async (devisId) => {
              const detail = await apiFetch(`${API}/devis/${devisId}/detail`).then(r => r.json());
              const lignesPrefill = (detail.version?.lignes || []).map((l, i) => ({
                ordre: i + 1, designation: l.designation, unite: l.unite || '',
                _libre: false, quantite: parseFloat(l.quantite), prix_unitaire_ht: parseFloat(l.prix_unitaire_ht)
              }));
              setForm({ affaire_id: detail.affaire_id, devis_id: devisId });
              setLignes(lignesPrefill.length > 0 ? lignesPrefill : [{ ordre:1, designation:'', unite:'', _libre:false, quantite:1, prix_unitaire_ht:0 }]);
              setPage('factures');
              setDevisSelectionne(null);
              setShowModal('facture');
            }}
            onEnvoyer={() => ouvrirModalEmail('devis', devisSelectionne)}
          />
        )}

        {page==='factures' && (
          <>
            <div style={s.pageHeader}>
              <h2 style={{...s.h2, margin:0}}>📄 Factures</h2>
              {peutEcrire && <button style={s.btn(COLORS.modules.factures)} onClick={() => { setForm({}); setLignes([{ordre:1,designation:'',unite:'',quantite:1,prix_unitaire_ht:0}]); setShowModal('facture'); }}>+ Nouvelle facture</button>}
            </div>

            {/* Barre de recherche */}
            <div style={{ display:'flex', gap:'12px', marginBottom:'16px', alignItems:'center' }}>
              <input
                style={{ ...s.input, marginBottom:0, flex:1, paddingLeft:'36px', backgroundImage:'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'16\' height=\'16\' fill=\'%239CA3AF\' viewBox=\'0 0 16 16\'%3E%3Cpath d=\'M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.099zm-5.242 1.656a5.5 5.5 0 1 1 0-11 5.5 5.5 0 0 1 0 11z\'/%3E%3C/svg%3E")', backgroundRepeat:'no-repeat', backgroundPosition:'10px center' }}
                placeholder="Numéro, affaire, client..."
                value={rechercheFactures.texte}
                onChange={e => setRechercheFactures(r => ({ ...r, texte: e.target.value }))}
              />
              <select
                style={{ ...s.input, marginBottom:0, width:'200px' }}
                value={rechercheFactures.statut}
                onChange={e => setRechercheFactures(r => ({ ...r, statut: e.target.value }))}
              >
                <option value="tous">Tous les statuts</option>
                <option value="brouillon">Brouillon</option>
                <option value="emise">Émise</option>
                <option value="partiellement_payee">Partiellement payée</option>
                <option value="payee">Payée</option>
                <option value="annulee">Annulée</option>
              </select>
              {(rechercheFactures.texte || rechercheFactures.statut !== 'tous') && (
                <button style={s.btnSm()} onClick={() => setRechercheFactures({ texte:'', statut:'tous' })}>✕ Effacer</button>
              )}
            </div>

            {(() => {
              const t = rechercheFactures.texte.toLowerCase();
              const facturesFiltrees = factures.filter(f => {
                const affaire = affaires.find(a => a.id === f.affaire_id);
                const clientNom = clients.find(c => c.id === affaire?.client_id)?.nom || '';
                const matchTexte = !t || [f.numero, affaire?.titre, affaire?.reference, clientNom].some(v => v && v.toLowerCase().includes(t));
                const matchStatut = rechercheFactures.statut === 'tous' || f.statut === rechercheFactures.statut;
                return matchTexte && matchStatut;
              });
              return (
                <>
                  <div style={{ fontSize:'13px', color:'#6B7280', marginBottom:'8px' }}>
                    {facturesFiltrees.length} facture{facturesFiltrees.length !== 1 ? 's' : ''} {factures.length !== facturesFiltrees.length ? `sur ${factures.length}` : ''}
                  </div>
                  {isMobile ? (
                    <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
                      {facturesFiltrees.length === 0
                        ? <div style={{ textAlign:'center', color:'#9CA3AF', padding:'32px' }}>Aucune facture trouvée</div>
                        : facturesFiltrees.map(f => (
                            <div key={f.id} style={s.mCard(COLORS.modules.factures)}>
                              <div style={s.mCardTop}>
                                <div style={{ fontWeight:'700', fontSize:'15px' }}>{f.numero}</div>
                                <span style={s.badge(f.statut)}>{f.statut}</span>
                              </div>
                              <div style={{ fontSize:'13px', color:'#6B7280', marginTop:'4px' }}>{affaires.find(a=>a.id===f.affaire_id)?.titre||'-'}</div>
                              <div style={{ display:'flex', justifyContent:'space-between', marginTop:'8px', fontSize:'14px' }}>
                                <span style={{ color:'#6B7280' }}>HT {parseFloat(f.montant_ht).toFixed(2)} €</span>
                                <span style={{ fontWeight:'700', color: parseFloat(f.solde_restant)>0 ? '#DC2626' : '#16A34A' }}>Solde {parseFloat(f.solde_restant).toFixed(2)} €</span>
                              </div>
                              {f.statut !== 'annulee' && avoirs.some(a => a.facture_origine_id === f.id) && (
                                <div style={{ marginTop:'6px' }}><span style={{ padding:'3px 10px', borderRadius:'20px', fontSize:'12px', fontWeight:'600', background:'#EDE9FE', color:'#7C3AED' }}>↩️ avoir émis</span></div>
                              )}
                              <div style={s.mCardActions}>
                                {peutEcrire && ['emise','partiellement_payee'].includes(f.statut) && (
                                  <button style={s.btnSm('#16A34A')} onClick={() => { setPaiementFacture(f); setFormPaiement({ montant: parseFloat(f.solde_restant).toFixed(2), mode_paiement:'virement', reference:'', numero_cheque:'', banque_cheque:'', tireur_cheque:'' }); }}>💰 Encaisser</button>
                                )}
                                {peutEcrire && ['emise','partiellement_payee','payee'].includes(f.statut) && (
                                  <button style={s.btnSm('#7C3AED')} onClick={() => ouvrirModalAvoir(f.id)}>↩️ Avoir</button>
                                )}
                                {['emise','partiellement_payee'].includes(f.statut) && (
                                  <button style={s.btnSm('#0EA5E9')} onClick={() => ouvrirModalEmail('facture', f.id)}>📧 Envoyer</button>
                                )}
                                <button style={s.btnSm('#6B7280')} onClick={() => imprimerFacture(f.id)}>🖨️ PDF</button>
                              </div>
                            </div>
                          ))
                      }
                    </div>
                  ) : (
                  <table style={s.table}>
                    <thead><tr>{['Numéro','Affaire','Montant HT','Solde restant','Statut','Actions'].map(h => <th key={h} style={s.th(COLORS.modules.factures)}>{h}</th>)}</tr></thead>
                    <tbody>
                      {facturesFiltrees.length === 0
                        ? <tr><td colSpan={6} style={{...s.td, textAlign:'center', color:'#9CA3AF', padding:'32px'}}>Aucune facture trouvée</td></tr>
                        : facturesFiltrees.map((f, i) => (
                            <tr key={f.id} style={{background: i%2===0?'white':'#F9FAFB'}}>
                              <td style={{...s.td, fontWeight:'600'}}>{f.numero}</td>
                              <td style={s.td}>{affaires.find(a=>a.id===f.affaire_id)?.titre||'-'}</td>
                              <td style={s.td}>{parseFloat(f.montant_ht).toFixed(2)} €</td>
                              <td style={s.td}>{parseFloat(f.solde_restant).toFixed(2)} €</td>
                              <td style={s.td}>
                                <span style={s.badge(f.statut)}>{f.statut}</span>
                                {f.statut !== 'annulee' && avoirs.some(a => a.facture_origine_id === f.id) && (
                                  <span style={{ display:'inline-block', marginLeft:'6px', padding:'3px 10px', borderRadius:'20px', fontSize:'12px', fontWeight:'600', background:'#EDE9FE', color:'#7C3AED' }}>↩️ avoir émis</span>
                                )}
                              </td>
                              <td style={s.td}>
                                {peutEcrire && ['emise','partiellement_payee'].includes(f.statut) && (
                                  <button style={{...s.btnSm('#16A34A'), marginRight:'6px'}} onClick={() => { setPaiementFacture(f); setFormPaiement({ montant: parseFloat(f.solde_restant).toFixed(2), mode_paiement:'virement', reference:'', numero_cheque:'', banque_cheque:'', tireur_cheque:'' }); }}>💰 Encaisser</button>
                                )}
                                {peutEcrire && ['emise','partiellement_payee','payee'].includes(f.statut) && (
                                  <button style={{...s.btnSm('#7C3AED'), marginRight:'6px'}} onClick={() => ouvrirModalAvoir(f.id)}>↩️ Avoir</button>
                                )}
                                {['emise','partiellement_payee'].includes(f.statut) && (
                                  <button style={{...s.btnSm('#0EA5E9'), marginRight:'6px'}} onClick={() => ouvrirModalEmail('facture', f.id)}>📧 Envoyer</button>
                                )}
                                <button style={s.btnSm('#6B7280')} onClick={() => imprimerFacture(f.id)}>🖨️ PDF</button>
                              </td>
                            </tr>
                          ))
                      }
                    </tbody>
                  </table>
                  )}
                  {/* Avoirs liés aux factures affichées */}
                  {avoirs.filter(a => facturesFiltrees.some(f => f.id === a.facture_origine_id)).length > 0 && (
                    <div style={{ marginTop: '24px' }}>
                      <h3 style={{ color: '#7C3AED', fontSize: '15px', margin: '0 0 10px 0' }}>↩️ Avoirs émis</h3>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr>
                          {['Numéro avoir', 'Facture d\'origine', 'Motif', 'Montant HT', 'Date', 'Actions'].map(h => (
                            <th key={h} style={{ ...s.th('#7C3AED'), fontSize: '13px' }}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {avoirs.filter(a => facturesFiltrees.some(f => f.id === a.facture_origine_id)).map((a, i) => (
                            <tr key={a.id} style={{ background: i%2===0 ? 'white' : '#FAF5FF' }}>
                              <td style={{ ...s.td, fontWeight: '600', color: '#7C3AED' }}>{a.numero}</td>
                              <td style={s.td}>{a.numero_facture_origine}</td>
                              <td style={{ ...s.td, color: '#6B7280', fontSize: '13px' }}>{a.motif}</td>
                              <td style={s.td}>{parseFloat(a.montant_ht).toFixed(2)} €</td>
                              <td style={{ ...s.td, fontSize: '13px', color: '#6B7280' }}>{new Date(a.date_emission).toLocaleDateString('fr-FR')}</td>
                              <td style={s.td}>
                                <button style={{...s.btnSm('#7C3AED'), marginRight:'6px'}} onClick={() => imprimerAvoir(a.id)}>🖨️ PDF</button>
                                <button style={{...s.btnSm('#0EA5E9'), marginRight:'6px'}} onClick={() => ouvrirModalEmail('avoir', a.id)}>📧 Envoyer</button>
                                {peutEcrire && <button style={s.btnSm('#16A34A')} onClick={() => ouvrirModalRemboursement(a.id)}>💸 Rembourser</button>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              );
            })()}
          </>
        )}

        {page==='agenda' && (
          <>
            <h2 style={s.h2}>📅 Agenda</h2>
            <Agenda evenements={evenements} clients={clients} affaires={affaires} onCreer={creerEvenement} onModifier={modifierEvenement} onSupprimer={supprimerEvenement}
              onOuvrirAffaire={(affaireId) => { const a = affaires.find(x => x.id === affaireId); if(a) { setAffaireSelectionnee(a); setPage('affaires'); } }} />
          </>
        )}

        {page==='admin' && utilisateur?.est_admin && (
          <Administration apiFetch={apiFetch}
            peutEcrire={peutEcrire} utilisateurCourant={utilisateur} />
        )}
      </div>

      {showProfil && (
        <Modal title="👤 Mon profil" onClose={() => setShowProfil(false)}>
          {/* Informations du compte */}
          <div style={{ background: '#F9FAFB', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px' }}>
              <div>
                <span style={{ color: '#6B7280', fontSize: '12px' }}>Nom</span>
                <div style={{ fontWeight: '600', color: COLORS.bleu }}>{utilisateur.nom} {utilisateur.prenom || ''}</div>
              </div>
              <div>
                <span style={{ color: '#6B7280', fontSize: '12px' }}>Email</span>
                <div style={{ fontWeight: '600', color: COLORS.bleu }}>{utilisateur.email}</div>
              </div>
              <div>
                <span style={{ color: '#6B7280', fontSize: '12px' }}>Profil</span>
                <div style={{ fontWeight: '600', color: COLORS.bleu }}>
                  {utilisateur.profil === 'admin' ? '🔑 Administrateur' : utilisateur.profil === 'gestionnaire' ? '👤 Gestionnaire' : '👁 Consultation'}
                </div>
              </div>
            </div>
          </div>

          {/* Changement de mot de passe */}
          <h4 style={{ color: COLORS.bleu, margin: '0 0 14px 0', fontSize: '15px' }}>🔒 Changer mon mot de passe</h4>

          {profilMsg.texte && (
            <div style={{ background: profilMsg.ok ? '#DCFCE7' : '#FEF2F2', color: profilMsg.ok ? '#16A34A' : '#DC2626', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '14px' }}>
              {profilMsg.texte}
            </div>
          )}

          <label style={{ display: 'block', fontSize: '13px', color: '#374151', fontWeight: '600', marginBottom: '4px' }}>Mot de passe actuel</label>
          <input
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px', marginBottom: '12px', boxSizing: 'border-box' }}
            type="password" placeholder="••••••••"
            value={formProfil.ancien}
            onChange={e => setFormProfil(f => ({ ...f, ancien: e.target.value }))}
          />

          <label style={{ display: 'block', fontSize: '13px', color: '#374151', fontWeight: '600', marginBottom: '4px' }}>Nouveau mot de passe <span style={{ color: '#6B7280', fontWeight: '400' }}>(8 caractères minimum)</span></label>
          <input
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px', marginBottom: '12px', boxSizing: 'border-box' }}
            type="password" placeholder="••••••••"
            value={formProfil.nouveau}
            onChange={e => setFormProfil(f => ({ ...f, nouveau: e.target.value }))}
          />

          <label style={{ display: 'block', fontSize: '13px', color: '#374151', fontWeight: '600', marginBottom: '4px' }}>Confirmer le nouveau mot de passe</label>
          <input
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px', marginBottom: '16px', boxSizing: 'border-box' }}
            type="password" placeholder="••••••••"
            value={formProfil.confirmation}
            onChange={e => setFormProfil(f => ({ ...f, confirmation: e.target.value }))}
          />

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={changerMonMotDePasse}
              style={{ background: COLORS.bleu, color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
              💾 Enregistrer
            </button>
            <button
              onClick={() => setShowProfil(false)}
              style={{ background: '#E5E7EB', color: '#374151', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>
              Fermer
            </button>
          </div>
        </Modal>
      )}

      {showModal==='client' && (
        <Modal title="➕ Nouveau client" onClose={() => { setShowModal(false); setForm({}); resetSiren(); }} wide={form.est_professionnel !== undefined}>

          {/* Étape 1 — choix du type */}
          {form.est_professionnel === undefined && (
            <div>
              <p style={{color:'#6B7280', fontSize:'14px', marginTop:0, marginBottom:'24px', textAlign:'center'}}>Quel type de client souhaitez-vous créer ?</p>
              <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px'}}>
                <button onClick={() => setForm({ est_professionnel: false })} style={{ background:'white', border:`2px solid ${COLORS.modules.clients}`, borderRadius:'12px', padding:'32px 16px', cursor:'pointer', textAlign:'center', transition:'all 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background='#EFF6FF'}
                  onMouseLeave={e => e.currentTarget.style.background='white'}>
                  <div style={{fontSize:'40px', marginBottom:'12px'}}>👤</div>
                  <div style={{fontWeight:'700', fontSize:'16px', color:COLORS.bleu}}>Particulier</div>
                  <div style={{fontSize:'12px', color:'#6B7280', marginTop:'6px'}}>Particulier — personne physique</div>
                </button>
                <button onClick={() => setForm({ est_professionnel: true })} style={{ background:'white', border:`2px solid ${COLORS.modules.clients}`, borderRadius:'12px', padding:'32px 16px', cursor:'pointer', textAlign:'center', transition:'all 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background='#EFF6FF'}
                  onMouseLeave={e => e.currentTarget.style.background='white'}>
                  <div style={{fontSize:'40px', marginBottom:'12px'}}>🏢</div>
                  <div style={{fontWeight:'700', fontSize:'16px', color:COLORS.bleu}}>Professionnel</div>
                  <div style={{fontSize:'12px', color:'#6B7280', marginTop:'6px'}}>Entreprise, artisan, société</div>
                </button>
              </div>
            </div>
          )}

          {/* Étape 2 — Particulier */}
          {form.est_professionnel === false && (
            <div>
              <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'20px'}}>
                <button onClick={() => { setForm({}); resetSiren(); }} style={{background:'none', border:'none', cursor:'pointer', color:'#6B7280', fontSize:'13px', padding:'4px 0'}}>← Changer de type</button>
                <span style={{background:'#EFF6FF', color:COLORS.bleu, borderRadius:'20px', padding:'3px 12px', fontSize:'13px', fontWeight:'600'}}>👤 Particulier</span>
              </div>
              <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px'}}>
                <div>
                  <label style={s.label}>Nom *</label>
                  <input style={s.input} value={form.nom||''} onChange={e => setForm({...form, nom:e.target.value})} placeholder="Nom de famille" />
                </div>
                <div>
                  <label style={s.label}>Prénom</label>
                  <input style={s.input} value={form.prenom||''} onChange={e => setForm({...form, prenom:e.target.value})} placeholder="Prénom" />
                </div>
                <div>
                  <label style={s.label}>Téléphone</label>
                  <input style={s.input} value={form.telephone||''} onChange={e => setForm({...form, telephone:e.target.value})} placeholder="06 XX XX XX XX" />
                </div>
                <div>
                  <label style={s.label}>Email</label>
                  <input style={s.input} value={form.email||''} onChange={e => setForm({...form, email:e.target.value})} placeholder="email@exemple.fr" />
                </div>
                <div style={{gridColumn:'1 / -1'}}>
                  <label style={s.label}>Adresse</label>
                  <AdresseAutocomplete apiFetch={apiFetch} value={form.adresse} inputStyle={s.input} placeholder="Numéro et rue"
                    onChangeText={v => setForm(f => ({...f, adresse: v}))}
                    onPick={sg => setForm(f => ({...f, adresse: sg.adresse || f.adresse, code_postal: sg.code_postal || f.code_postal, ville: sg.ville || f.ville}))} />
                </div>
                <div>
                  <label style={s.label}>Code postal</label>
                  <input style={s.input} value={form.code_postal||''} onChange={e => setForm({...form, code_postal:e.target.value})} placeholder="31000" />
                </div>
                <div>
                  <label style={s.label}>Ville</label>
                  <input style={s.input} value={form.ville||''} onChange={e => setForm({...form, ville:e.target.value})} placeholder="Toulouse" />
                </div>
              </div>
              <button style={{...s.btn(COLORS.modules.clients), width:'100%', padding:'12px', marginTop:'16px'}} onClick={creerClient} disabled={loading}>
                {loading ? 'Création...' : '✅ Créer le client'}
              </button>
            </div>
          )}

          {/* Étape 2 — Professionnel */}
          {form.est_professionnel === true && (
            <div>
              <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'20px'}}>
                <button onClick={() => { setForm({}); resetSiren(); }} style={{background:'none', border:'none', cursor:'pointer', color:'#6B7280', fontSize:'13px', padding:'4px 0'}}>← Changer de type</button>
                <span style={{background:'#EFF6FF', color:COLORS.bleu, borderRadius:'20px', padding:'3px 12px', fontSize:'13px', fontWeight:'600'}}>🏢 Professionnel</span>
              </div>
              <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px'}}>
                <div style={{gridColumn:'1 / -1', background:'#F0FDFA', border:'1px solid #99F6E4', borderRadius:'8px', padding:'12px'}}>
                  <label style={s.label}>SIREN / SIRET ou nom <span style={{fontWeight:400}}>— recherche automatique des infos de l'entreprise</span></label>
                  <div style={{display:'flex', gap:'8px'}}>
                    <input style={{...s.input, flex:1}} value={sirenQuery} placeholder="SIREN (9 chiffres), SIRET ou nom"
                      onChange={e => setSirenQuery(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); rechercherEntrepriseClient(); } }} />
                    <button type="button" style={{...s.btn('#0D9488'), whiteSpace:'nowrap'}} onClick={rechercherEntrepriseClient} disabled={rechercheSiren}>
                      {rechercheSiren ? '⏳' : '🔍 Rechercher'}
                    </button>
                  </div>
                  {sirenErreur && <div style={{color:'#DC2626', fontSize:'12px', marginTop:'6px'}}>{sirenErreur}</div>}
                  {sirenInfo && <div style={{fontSize:'12px', marginTop:'6px', color:'#374151'}}>
                    {sirenInfo.actif ? '✅' : '⚠️'} <strong>{sirenInfo.denomination}</strong>
                    {sirenInfo.adresse ? ` — ${sirenInfo.adresse}` : ''}
                    {!sirenInfo.actif ? ' (établissement cessé ?)' : ''}
                  </div>}
                </div>
                <div style={{gridColumn:'1 / -1'}}>
                  <label style={s.label}>Nom de l'entreprise *</label>
                  <input style={s.input} value={form.nom||''} onChange={e => setForm({...form, nom:e.target.value})} placeholder="Raison sociale" />
                </div>
                <div>
                  <label style={s.label}>SIRET</label>
                  <input style={s.input} value={form.siret||''} onChange={e => setForm({...form, siret:e.target.value})} placeholder="000 000 000 00000" />
                  {!form.siret && (
                    <div style={{fontSize:'11px', color:'#D97706', marginTop:'4px'}}>
                      ℹ️ Deviendra obligatoire pour facturer ce client (réforme facturation électronique, échéance 2027)
                    </div>
                  )}
                </div>
                <div>
                  <label style={s.label}>Téléphone</label>
                  <input style={s.input} value={form.telephone||''} onChange={e => setForm({...form, telephone:e.target.value})} placeholder="05 XX XX XX XX" />
                </div>
                <div style={{gridColumn:'1 / -1', borderTop:'1px solid #F3F4F6', paddingTop:'12px', marginTop:'4px'}}>
                  <div style={{fontSize:'13px', fontWeight:'600', color:'#374151', marginBottom:'10px'}}>Contact principal</div>
                </div>
                <div>
                  <label style={s.label}>Nom</label>
                  <input style={s.input} value={form.nom_entreprise||''} onChange={e => setForm({...form, nom_entreprise:e.target.value})} placeholder="Nom" />
                </div>
                <div>
                  <label style={s.label}>Prénom</label>
                  <input style={s.input} value={form.prenom||''} onChange={e => setForm({...form, prenom:e.target.value})} placeholder="Prénom" />
                </div>
                <div>
                  <label style={s.label}>Poste / Rôle</label>
                  <input style={s.input} value={form.contact_1_poste||''} onChange={e => setForm({...form, contact_1_poste:e.target.value})} placeholder="Ex: Responsable chantiers" />
                </div>
                <div>
                  <label style={s.label}>Téléphone direct</label>
                  <input style={s.input} value={form.contact_1_telephone||''} onChange={e => setForm({...form, contact_1_telephone:e.target.value})} placeholder="06 XX XX XX XX" />
                </div>
                <div style={{gridColumn:'1 / -1'}}>
                  <label style={s.label}>Email</label>
                  <input style={s.input} value={form.email||''} onChange={e => setForm({...form, email:e.target.value})} placeholder="contact@entreprise.fr" />
                </div>
                <div style={{gridColumn:'1 / -1', borderTop:'1px solid #F3F4F6', paddingTop:'12px', marginTop:'4px'}}>
                  <div style={{fontSize:'13px', fontWeight:'600', color:'#374151', marginBottom:'10px'}}>Adresse du siège</div>
                </div>
                <div style={{gridColumn:'1 / -1'}}>
                  <label style={s.label}>Adresse</label>
                  <AdresseAutocomplete apiFetch={apiFetch} value={form.adresse} inputStyle={s.input} placeholder="Numéro et rue"
                    onChangeText={v => setForm(f => ({...f, adresse: v}))}
                    onPick={sg => setForm(f => ({...f, adresse: sg.adresse || f.adresse, code_postal: sg.code_postal || f.code_postal, ville: sg.ville || f.ville}))} />
                </div>
                <div>
                  <label style={s.label}>Code postal</label>
                  <input style={s.input} value={form.code_postal||''} onChange={e => setForm({...form, code_postal:e.target.value})} placeholder="31000" />
                </div>
                <div>
                  <label style={s.label}>Ville</label>
                  <input style={s.input} value={form.ville||''} onChange={e => setForm({...form, ville:e.target.value})} placeholder="Toulouse" />
                </div>
              </div>
              <button style={{...s.btn(COLORS.modules.clients), width:'100%', padding:'12px', marginTop:'16px'}} onClick={creerClient} disabled={loading}>
                {loading ? 'Création...' : '✅ Créer le client'}
              </button>
            </div>
          )}
        </Modal>
      )}

      {showModal==='affaire' && (
        <Modal title="➕ Nouvelle affaire" onClose={() => setShowModal(false)}>
          <label style={s.label}>Titre *</label>
          <input style={s.input} value={form.titre||''} onChange={e => setForm({...form, titre:e.target.value})} placeholder="Ex: Cuisine Dupont" />
          <label style={s.label}>Client *</label>
          <select style={s.input} value={form.client_id||''} onChange={e => setForm({...form, client_id:e.target.value})}>
            <option value=''>-- Sélectionner un client --</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.nom} {c.prenom||''}</option>)}
          </select>
          <label style={s.label}>Description</label>
          <input style={s.input} value={form.description||''} onChange={e => setForm({...form, description:e.target.value})} placeholder="Description des travaux" />
          <label style={s.label}>Ville chantier</label>
          <input style={s.input} value={form.ville_chantier||''} onChange={e => setForm({...form, ville_chantier:e.target.value})} placeholder="Ville" />
          <button style={{...s.btn(COLORS.modules.affaires), width:'100%', padding:'12px'}} onClick={creerAffaire} disabled={loading}>
            {loading ? 'Création...' : "✅ Créer l'affaire"}
          </button>
        </Modal>
      )}

      {showModal==='devis' && (
        <Modal title="➕ Nouveau devis" onClose={() => setShowModal(false)} wide={true}>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px'}}>
            <div>
              <label style={s.label}>Affaire *</label>
              <select style={s.input} value={form.affaire_id||''} onChange={e => setForm({...form, affaire_id:e.target.value})}>
                <option value=''>-- Sélectionner --</option>
                {affaires.map(a => <option key={a.id} value={a.id}>{a.reference} - {a.titre}</option>)}
              </select>
            </div>
            <div>
              <label style={s.label}>Validité jusqu'au</label>
              <input style={s.input} type="date" value={form.date_echeance_validite||''} onChange={e => setForm({...form, date_echeance_validite:e.target.value})} />
            </div>
            <div>
              <label style={s.label}>Notes</label>
              <input style={s.input} value={form.notes||''} onChange={e => setForm({...form, notes:e.target.value})} placeholder="Notes optionnelles" />
            </div>
          </div>
          {lignesForm(COLORS.modules.devis)}
          <button style={{...s.btn(COLORS.modules.devis), width:'100%', padding:'12px', marginTop:'16px'}} onClick={creerDevis} disabled={loading}>
            {loading ? 'Création...' : '✅ Créer le devis'}
          </button>
        </Modal>
      )}

      {showModal==='facture' && (
        <Modal title="➕ Nouvelle facture" onClose={() => setShowModal(false)} wide={true}>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px'}}>
            <div>
              <label style={s.label}>Affaire *</label>
              <select style={s.input} value={form.affaire_id||''} onChange={e => setForm({...form, affaire_id:e.target.value})}>
                <option value=''>-- Sélectionner --</option>
                {affaires.map(a => <option key={a.id} value={a.id}>{a.reference} - {a.titre}</option>)}
              </select>
            </div>
            <div>
              <label style={s.label}>Devis lié (optionnel)</label>
              <select style={s.input} value={form.devis_id||''} onChange={e => setForm({...form, devis_id:e.target.value})}>
                <option value=''>-- Aucun devis --</option>
                {devis.map(d => <option key={d.id} value={d.id}>{d.reference}</option>)}
              </select>
            </div>
            <div>
              <label style={s.label}>Notes</label>
              <input style={s.input} value={form.notes||''} onChange={e => setForm({...form, notes:e.target.value})} placeholder="Notes optionnelles" />
            </div>
          </div>
          {form.devis_id && (() => {
            const devisSel = devis.find(d => d.id === parseInt(form.devis_id));
            const acomptesDuDevis = factures.filter(f => f.devis_id === parseInt(form.devis_id) && f.type === 'acompte');
            if (acomptesDuDevis.length === 0) return null;
            const totalDevis = parseFloat(devisSel?.montant_ht || devisSel?.version?.montant_ht || 0);
            const totalAcomptes = acomptesDuDevis.reduce((s,f) => s + parseFloat(f.montant_ht), 0);
            const netDu = Math.max(totalDevis - totalAcomptes, 0);
            return (
              <div style={{background:'#FEF9C3', border:'1px solid #FDE047', borderRadius:'8px', padding:'12px 16px', marginTop:'14px', fontSize:'13px', color:'#713F12'}}>
                <div style={{fontWeight:'700', marginBottom:'6px'}}>ℹ️ Acompte(s) déjà facturé(s) sur ce devis</div>
                <div style={{marginBottom:'4px'}}>
                  {acomptesDuDevis.map((a,i) => (
                    <span key={a.id}>
                      {i > 0 && ' · '}
                      <strong>{a.numero}</strong> : {parseFloat(a.montant_ht).toFixed(2)} € {a.statut === 'payee' ? '(✅ payé)' : a.statut === 'partiellement_payee' ? '(⚠️ partiel)' : '(en attente)'}
                    </span>
                  ))}
                </div>
                <div style={{borderTop:'1px solid #FDE047', paddingTop:'6px', marginTop:'6px'}}>
                  Total devis : <strong>{totalDevis.toFixed(2)} €</strong> − Acompte(s) : <strong>{totalAcomptes.toFixed(2)} €</strong> = <span style={{color:'#15803D', fontWeight:'700'}}>Net dû à l'émission : {netDu.toFixed(2)} €</span>
                </div>
              </div>
            );
          })()}
          {lignesForm(COLORS.modules.factures)}
          <button style={{...s.btn(COLORS.modules.factures), width:'100%', padding:'12px', marginTop:'16px'}} onClick={creerFacture} disabled={loading}>
            {loading ? 'Émission...' : '📤 Émettre la facture'}
          </button>
        </Modal>
      )}

      {acompteDevis && (
        <Modal title={`📥 Facture d'acompte — ${acompteDevis.reference}`} onClose={() => setAcompteDevis(null)}>
          <div style={{marginBottom:'16px', background:'#F0F9FF', border:'1px solid #BAE6FD', borderRadius:'8px', padding:'12px 16px'}}>
            <div style={{fontSize:'13px', color:'#0369A1', marginBottom:'2px'}}>Total devis HT</div>
            <div style={{fontSize:'22px', fontWeight:'700', color:'#0369A1'}}>{acompteDevis.montantHT.toFixed(2)} €</div>
          </div>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px', marginBottom:'20px'}}>
            <div>
              <label style={{display:'block', fontSize:'13px', color:'#374151', fontWeight:'600', marginBottom:'4px'}}>Pourcentage</label>
              <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                <input type="number" min="1" max="100" style={{width:'80px', padding:'8px 10px', border:'1px solid #D1D5DB', borderRadius:'6px', fontSize:'16px', fontWeight:'700', textAlign:'center'}}
                  value={formAcompte.pct}
                  onChange={e => {
                    const pct = parseFloat(e.target.value) || 0;
                    setFormAcompte({ pct, montant: (acompteDevis.montantHT * pct / 100).toFixed(2) });
                  }} />
                <span style={{fontSize:'20px', color:'#6B7280'}}>%</span>
              </div>
            </div>
            <div>
              <label style={{display:'block', fontSize:'13px', color:'#374151', fontWeight:'600', marginBottom:'4px'}}>Montant HT</label>
              <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                <input type="number" min="0" step="0.01" style={{width:'120px', padding:'8px 10px', border:'1px solid #D1D5DB', borderRadius:'6px', fontSize:'16px', fontWeight:'700', textAlign:'center'}}
                  value={formAcompte.montant}
                  onChange={e => {
                    const montant = parseFloat(e.target.value) || 0;
                    const pct = acompteDevis.montantHT > 0 ? Math.round(montant / acompteDevis.montantHT * 100) : 0;
                    setFormAcompte({ pct, montant: e.target.value });
                  }} />
                <span style={{fontSize:'16px', color:'#6B7280'}}>€</span>
              </div>
            </div>
          </div>
          <button onClick={creerAcompte} disabled={loading || !formAcompte.montant || parseFloat(formAcompte.montant) <= 0}
            style={{width:'100%', background:'#1E3A5F', color:'white', border:'none', padding:'12px', borderRadius:'8px', fontSize:'15px', fontWeight:'700', cursor:'pointer'}}>
            {loading ? 'Émission...' : `📤 Émettre l'acompte de ${parseFloat(formAcompte.montant||0).toFixed(2)} €`}
          </button>
        </Modal>
      )}

      {paiementFacture && (
        <Modal title={`💰 Encaisser — ${paiementFacture.numero}`} onClose={() => setPaiementFacture(null)}>
          <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:'8px', padding:'12px 16px', marginBottom:'20px', fontSize:'14px' }}>
            <div style={{ display:'flex', justifyContent:'space-between' }}>
              <span style={{ color:'#6B7280' }}>Montant total HT</span>
              <strong>{parseFloat(paiementFacture.montant_ht).toFixed(2)} €</strong>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', marginTop:'4px' }}>
              <span style={{ color:'#6B7280' }}>Solde restant dû</span>
              <strong style={{ color:'#DC2626', fontSize:'16px' }}>{parseFloat(paiementFacture.solde_restant).toFixed(2)} €</strong>
            </div>
          </div>

          <label style={{ display:'block', fontSize:'13px', color:'#374151', fontWeight:'600', marginBottom:'4px' }}>Montant encaissé (€) *</label>
          <input
            style={{ width:'100%', padding:'10px 14px', border:'1px solid #D1D5DB', borderRadius:'8px', fontSize:'18px', fontWeight:'700', textAlign:'center', boxSizing:'border-box', marginBottom:'14px' }}
            type="number" step="0.01" min="0.01"
            value={formPaiement.montant}
            onChange={e => setFormPaiement(f => ({...f, montant: e.target.value}))}
            autoFocus
          />

          <label style={{ display:'block', fontSize:'13px', color:'#374151', fontWeight:'600', marginBottom:'4px' }}>Mode de paiement</label>
          <select style={{ width:'100%', padding:'8px 12px', border:'1px solid #D1D5DB', borderRadius:'6px', fontSize:'14px', marginBottom:'14px', boxSizing:'border-box' }}
            value={formPaiement.mode_paiement}
            onChange={e => setFormPaiement(f => ({...f, mode_paiement: e.target.value, numero_cheque:'', banque_cheque:'', tireur_cheque:''}))}>
            <option value="virement">Virement</option>
            <option value="cheque">Chèque</option>
            <option value="especes">Espèces</option>
          </select>

          {formPaiement.mode_paiement === 'especes' && parseFloat(formPaiement.montant) > 1000 && (
            <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:'6px', padding:'8px 12px', marginBottom:'14px', fontSize:'13px', color:'#991B1B' }}>
              ⚠️ Plafond légal espèces dépassé : maximum 1 000 € par transaction (art. L112-6 CMF).
            </div>
          )}

          {formPaiement.mode_paiement === 'cheque' && (
            <div style={{ background:'#F9FAFB', border:'1px solid #E5E7EB', borderRadius:'6px', padding:'12px', marginBottom:'14px' }}>
              <label style={{ display:'block', fontSize:'13px', color:'#374151', fontWeight:'600', marginBottom:'4px' }}>N° chèque *</label>
              <input
                style={{ width:'100%', padding:'8px 12px', border:'1px solid #D1D5DB', borderRadius:'6px', fontSize:'14px', marginBottom:'10px', boxSizing:'border-box' }}
                value={formPaiement.numero_cheque}
                onChange={e => setFormPaiement(f => ({...f, numero_cheque: e.target.value}))}
                placeholder="ex : 0001234"
              />
              <label style={{ display:'block', fontSize:'13px', color:'#374151', fontWeight:'600', marginBottom:'4px' }}>Banque émettrice *</label>
              <input
                style={{ width:'100%', padding:'8px 12px', border:'1px solid #D1D5DB', borderRadius:'6px', fontSize:'14px', marginBottom:'10px', boxSizing:'border-box' }}
                value={formPaiement.banque_cheque}
                onChange={e => setFormPaiement(f => ({...f, banque_cheque: e.target.value}))}
                placeholder="ex : Crédit Agricole"
              />
              <label style={{ display:'block', fontSize:'13px', color:'#374151', fontWeight:'600', marginBottom:'4px' }}>Tireur (nom inscrit sur le chèque) *</label>
              <input
                style={{ width:'100%', padding:'8px 12px', border:'1px solid #D1D5DB', borderRadius:'6px', fontSize:'14px', boxSizing:'border-box' }}
                value={formPaiement.tireur_cheque}
                onChange={e => setFormPaiement(f => ({...f, tireur_cheque: e.target.value}))}
                placeholder="ex : Jean Dupont"
              />
            </div>
          )}

          {formPaiement.mode_paiement === 'especes' && (
            <div style={{ background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:'6px', padding:'8px 12px', marginBottom:'14px', fontSize:'13px', color:'#92400E' }}>
              ℹ️ Encaissement automatiquement reporté au registre des espèces (obligation légale).
            </div>
          )}

          <label style={{ display:'block', fontSize:'13px', color:'#374151', fontWeight:'600', marginBottom:'4px' }}>Référence (optionnel)</label>
          <input
            style={{ width:'100%', padding:'8px 12px', border:'1px solid #D1D5DB', borderRadius:'6px', fontSize:'14px', marginBottom:'20px', boxSizing:'border-box' }}
            placeholder={formPaiement.mode_paiement === 'virement' ? 'Référence virement, libellé bancaire...' : 'Note libre'}
            value={formPaiement.reference}
            onChange={e => setFormPaiement(f => ({...f, reference: e.target.value}))}
          />

          <div style={{ display:'flex', gap:'8px' }}>
            <button onClick={enregistrerPaiement} disabled={loading}
              style={{ flex:1, background:'#16A34A', color:'white', border:'none', padding:'12px', borderRadius:'8px', cursor:'pointer', fontSize:'15px', fontWeight:'700' }}>
              {loading ? 'Enregistrement...' : '✅ Valider le paiement'}
            </button>
            <button onClick={() => setPaiementFacture(null)}
              style={{ background:'#E5E7EB', color:'#374151', border:'none', padding:'12px 20px', borderRadius:'8px', cursor:'pointer', fontSize:'14px' }}>
              Annuler
            </button>
          </div>
        </Modal>
      )}

      {remboursementAvoir && (
        <Modal title={`💸 Rembourser l'avoir — ${remboursementAvoir.numero}`} onClose={() => setRemboursementAvoir(null)}>
          <div style={{ background:'#FDF4FF', border:'1px solid #E9D5FF', borderRadius:'8px', padding:'12px 16px', marginBottom:'20px', fontSize:'14px' }}>
            <div style={{ display:'flex', justifyContent:'space-between' }}>
              <span style={{ color:'#6B7280' }}>Montant total avoir</span>
              <strong>{parseFloat(remboursementAvoir.montant_total).toFixed(2)} €</strong>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', marginTop:'4px' }}>
              <span style={{ color:'#6B7280' }}>Déjà remboursé</span>
              <strong>{parseFloat(remboursementAvoir.deja_rembourse).toFixed(2)} €</strong>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', marginTop:'4px' }}>
              <span style={{ color:'#6B7280' }}>Reste à rembourser</span>
              <strong style={{ color:'#7C3AED', fontSize:'16px' }}>{remboursementAvoir.reste_a_rembourser.toFixed(2)} €</strong>
            </div>
          </div>

          <div style={{ background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:'6px', padding:'8px 12px', marginBottom:'14px', fontSize:'12px', color:'#1E40AF' }}>
            ℹ️ Un remboursement effectif (en monnaie) figure au livre des recettes en montant négatif à la date d'aujourd'hui. Si l'avoir est compensé sur une facture future, n'enregistrez pas de remboursement ici.
          </div>

          <label style={{ display:'block', fontSize:'13px', color:'#374151', fontWeight:'600', marginBottom:'4px' }}>Montant remboursé (€) *</label>
          <input
            style={{ width:'100%', padding:'10px 14px', border:'1px solid #D1D5DB', borderRadius:'8px', fontSize:'18px', fontWeight:'700', textAlign:'center', boxSizing:'border-box', marginBottom:'14px' }}
            type="number" step="0.01" min="0.01"
            value={formRemboursement.montant}
            onChange={e => setFormRemboursement(f => ({...f, montant: e.target.value}))}
            autoFocus
          />

          <label style={{ display:'block', fontSize:'13px', color:'#374151', fontWeight:'600', marginBottom:'4px' }}>Mode de remboursement</label>
          <select style={{ width:'100%', padding:'8px 12px', border:'1px solid #D1D5DB', borderRadius:'6px', fontSize:'14px', marginBottom:'14px', boxSizing:'border-box' }}
            value={formRemboursement.mode_paiement}
            onChange={e => setFormRemboursement(f => ({...f, mode_paiement: e.target.value, numero_cheque:'', banque_cheque:'', tireur_cheque:''}))}>
            <option value="virement">Virement</option>
            <option value="cheque">Chèque</option>
            <option value="especes">Espèces</option>
          </select>

          {formRemboursement.mode_paiement === 'especes' && parseFloat(formRemboursement.montant) > 1000 && (
            <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:'6px', padding:'8px 12px', marginBottom:'14px', fontSize:'13px', color:'#991B1B' }}>
              ⚠️ Plafond légal espèces dépassé : maximum 1 000 € par transaction (art. L112-6 CMF).
            </div>
          )}

          {formRemboursement.mode_paiement === 'cheque' && (
            <div style={{ background:'#F9FAFB', border:'1px solid #E5E7EB', borderRadius:'6px', padding:'12px', marginBottom:'14px' }}>
              <label style={{ display:'block', fontSize:'13px', color:'#374151', fontWeight:'600', marginBottom:'4px' }}>N° chèque *</label>
              <input
                style={{ width:'100%', padding:'8px 12px', border:'1px solid #D1D5DB', borderRadius:'6px', fontSize:'14px', marginBottom:'10px', boxSizing:'border-box' }}
                value={formRemboursement.numero_cheque}
                onChange={e => setFormRemboursement(f => ({...f, numero_cheque: e.target.value}))}
                placeholder="ex : 0001234"
              />
              <label style={{ display:'block', fontSize:'13px', color:'#374151', fontWeight:'600', marginBottom:'4px' }}>Banque émettrice *</label>
              <input
                style={{ width:'100%', padding:'8px 12px', border:'1px solid #D1D5DB', borderRadius:'6px', fontSize:'14px', marginBottom:'10px', boxSizing:'border-box' }}
                value={formRemboursement.banque_cheque}
                onChange={e => setFormRemboursement(f => ({...f, banque_cheque: e.target.value}))}
                placeholder="ex : Société Générale"
              />
              <label style={{ display:'block', fontSize:'13px', color:'#374151', fontWeight:'600', marginBottom:'4px' }}>Tireur (nom inscrit sur le chèque) *</label>
              <input
                style={{ width:'100%', padding:'8px 12px', border:'1px solid #D1D5DB', borderRadius:'6px', fontSize:'14px', boxSizing:'border-box' }}
                value={formRemboursement.tireur_cheque}
                onChange={e => setFormRemboursement(f => ({...f, tireur_cheque: e.target.value}))}
                placeholder="ex : map-menuiserie"
              />
            </div>
          )}

          {formRemboursement.mode_paiement === 'especes' && (
            <div style={{ background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:'6px', padding:'8px 12px', marginBottom:'14px', fontSize:'13px', color:'#92400E' }}>
              ℹ️ Décaissement automatiquement reporté au registre des espèces (obligation légale).
            </div>
          )}

          <label style={{ display:'block', fontSize:'13px', color:'#374151', fontWeight:'600', marginBottom:'4px' }}>Référence (optionnel)</label>
          <input
            style={{ width:'100%', padding:'8px 12px', border:'1px solid #D1D5DB', borderRadius:'6px', fontSize:'14px', marginBottom:'10px', boxSizing:'border-box' }}
            placeholder={formRemboursement.mode_paiement === 'virement' ? 'Référence virement, libellé bancaire...' : 'Note libre'}
            value={formRemboursement.reference}
            onChange={e => setFormRemboursement(f => ({...f, reference: e.target.value}))}
          />

          <label style={{ display:'block', fontSize:'13px', color:'#374151', fontWeight:'600', marginBottom:'4px' }}>Notes (optionnel)</label>
          <textarea
            style={{ width:'100%', padding:'8px 12px', border:'1px solid #D1D5DB', borderRadius:'6px', fontSize:'14px', marginBottom:'20px', boxSizing:'border-box', resize:'vertical', minHeight:'60px' }}
            placeholder="Ex : remboursement après accord client du JJ/MM/AAAA"
            value={formRemboursement.notes}
            onChange={e => setFormRemboursement(f => ({...f, notes: e.target.value}))}
          />

          <div style={{ display:'flex', gap:'8px' }}>
            <button onClick={enregistrerRemboursement} disabled={loading}
              style={{ flex:1, background:'#7C3AED', color:'white', border:'none', padding:'12px', borderRadius:'8px', cursor:'pointer', fontSize:'15px', fontWeight:'700' }}>
              {loading ? 'Enregistrement...' : '✅ Valider le remboursement'}
            </button>
            <button onClick={() => setRemboursementAvoir(null)}
              style={{ background:'#E5E7EB', color:'#374151', border:'none', padding:'12px 20px', borderRadius:'8px', cursor:'pointer', fontSize:'14px' }}>
              Annuler
            </button>
          </div>
        </Modal>
      )}

      {showModal==='avoir' && avoirFacture && (
        <Modal title={`↩️ Créer un avoir — Facture ${avoirFacture.numero}`} onClose={() => { setShowModal(false); setAvoirFacture(null); }} wide={true}>
          <div style={{ background: '#FDF4FF', border: '1px solid #E9D5FF', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: '#7C3AED' }}>
            Un avoir annule totalement ou partiellement une facture émise. Il doit mentionner le numéro de la facture d'origine et indiquer un motif.
          </div>

          <label style={s.label}>Motif de l'avoir *</label>
          <input
            style={{ ...s.input, marginBottom: '16px' }}
            value={avoirMotif}
            onChange={e => setAvoirMotif(e.target.value)}
            placeholder="Ex : Erreur de facturation, prestation non réalisée, remise accordée..."
            autoFocus
          />

          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 8px 0' }}>
            Lignes de l'avoir (pré-remplies depuis la facture d'origine, modifiables) :
          </p>

          {/* Lignes avoir */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '8px' }}>
            <thead>
              <tr>
                {['Désignation', 'Unité', 'Qté', 'Prix HT', 'Total', ''].map(h => (
                  <th key={h} style={{ ...s.th('#7C3AED'), padding: '8px 10px', fontSize: '12px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {avoirLignes.map((l, i) => (
                <tr key={i}>
                  <td style={{ padding: '4px' }}>
                    <input style={s.inputSm} value={l.designation} onChange={e => setAvoirLignes(prev => prev.map((x, j) => j===i ? {...x, designation: e.target.value} : x))} />
                  </td>
                  <td style={{ padding: '4px', width: '110px' }}>
                    {l._libre
                      ? <input style={s.inputSm} value={l.unite} onChange={e => setAvoirLignes(prev => prev.map((x, j) => j===i ? {...x, unite: e.target.value} : x))} />
                      : <select style={s.inputSm} value={l.unite} onChange={e => {
                          const v = e.target.value;
                          setAvoirLignes(prev => prev.map((x, j) => j===i ? {...x, unite: v==='__libre__' ? '' : v, _libre: v==='__libre__'} : x));
                        }}>
                          {UNITES.map(u => <option key={u} value={u}>{u}</option>)}
                          <option value="">—</option>
                          <option value="__libre__">Saisie libre…</option>
                        </select>
                    }
                  </td>
                  <td style={{ padding: '4px', width: '70px' }}>
                    <input style={s.inputSm} type="number" value={l.quantite} onChange={e => setAvoirLignes(prev => prev.map((x, j) => j===i ? {...x, quantite: e.target.value} : x))} />
                  </td>
                  <td style={{ padding: '4px', width: '90px' }}>
                    <input style={s.inputSm} type="number" value={l.prix_unitaire_ht} onChange={e => setAvoirLignes(prev => prev.map((x, j) => j===i ? {...x, prix_unitaire_ht: e.target.value} : x))} />
                  </td>
                  <td style={{ padding: '4px', width: '90px', textAlign: 'right', fontWeight: '600', fontSize: '13px' }}>
                    {((parseFloat(l.quantite)||0)*(parseFloat(l.prix_unitaire_ht)||0)).toFixed(2)} €
                  </td>
                  <td style={{ padding: '4px', width: '30px' }}>
                    {avoirLignes.length > 1 && <button style={{ ...s.btnSm(), color: '#DC2626' }} onClick={() => setAvoirLignes(prev => prev.filter((_, j) => j!==i))}>✕</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button style={{ ...s.btnSm(), marginBottom: '16px' }} onClick={() => setAvoirLignes(prev => [...prev, { ordre: prev.length+1, designation: '', unite: '', _libre: false, quantite: 1, prix_unitaire_ht: 0 }])}>+ Ajouter une ligne</button>

          <div style={{ textAlign: 'right', fontSize: '16px', fontWeight: '700', color: '#7C3AED', marginBottom: '16px' }}>
            Total avoir HT : {avoirLignes.reduce((s, l) => s + (parseFloat(l.quantite)||0)*(parseFloat(l.prix_unitaire_ht)||0), 0).toFixed(2)} €
          </div>

          <button style={{ ...s.btn('#7C3AED'), width: '100%', padding: '12px' }} onClick={creerAvoir} disabled={loading}>
            {loading ? 'Création...' : '✅ Émettre l\'avoir'}
          </button>
        </Modal>
      )}

      {emailDocument && (
        <ModalEmail
          emailDocument={emailDocument}
          apiFetch={apiFetch}
          onClose={() => setEmailDocument(null)}
          showMsg={showMsg}
        />
      )}
    </div>
  );
}

export { Modal };
export default App;
